// src/common/MeshBackground.tsx
import { useCallback, useEffect, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var MOBILE_BREAKPOINT = 768;
var POLYGON_SIDES = 6;
var SHARED_ROTATION = Math.PI / 6;
var BASE_STROKE_ALPHA = [0.055, 0.075, 0.1];
var BASE_STROKE_WIDTH = [1, 1.2, 1.35];
var INTERACTION_RADIUS = 180;
var EDGE_TRIGGER_DISTANCE = 44;
var MAX_PARALLAX = 10;
var EASING_SMOOTHING = 0.12;
var MAX_PULSES = 24;
var CORNER_ROUNDING = 0.1;
var CORNER_BEZIER_STEPS = 5;
var CURSOR_TRAIL_RADIUS = 50;
var CURSOR_TRAIL_MIN_LENGTH = 0.08;
var CURSOR_TRAIL_SPEED_LENGTH = 0.2;
var CIRCLE_SPEED_THRESHOLD = 0.22;
var CIRCLE_TURN_RATE_THRESHOLD = 22e-4;
var RELAY_RESONANCE_THRESHOLD = 0.78;
var MAX_PULSE_RELAY_PER_EVENT = 1;
var MIN_RELAY_COOLDOWN_MS = 260;
var MAX_RELAY_COOLDOWN_MS = 380;
var DESKTOP_ROWS = 2;
var DESKTOP_COLS = 2;
var MOBILE_ROWS = 2;
var MOBILE_COLS = 1;
var ANCHOR_INSET_X = 0.08;
var DESKTOP_ANCHOR_INSET_Y = 0.12;
var MOBILE_ANCHOR_INSET_Y = 0.18;
function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
function mediaMatches(query) {
  if (typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}
function pointAtT(x1, y1, x2, y2, t) {
  return {
    x: x1 + (x2 - x1) * t,
    y: y1 + (y2 - y1) * t
  };
}
function distanceBetween(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}
function normalizeAngleDelta(delta) {
  let next = delta;
  while (next > Math.PI) next -= Math.PI * 2;
  while (next < -Math.PI) next += Math.PI * 2;
  return next;
}
function buildSmoothPath(vertices) {
  const n = vertices.length;
  if (n < 3) return vertices;
  const path = [];
  for (let i = 0; i < n; i++) {
    const prev = vertices[(i - 1 + n) % n];
    const curr = vertices[i];
    const next = vertices[(i + 1) % n];
    const prevLen = distanceBetween(curr, prev);
    const nextLen = distanceBetween(curr, next);
    const r = Math.min(prevLen, nextLen) * Math.min(CORNER_ROUNDING, 0.4);
    const entryX = curr.x + (prev.x - curr.x) / prevLen * r;
    const entryY = curr.y + (prev.y - curr.y) / prevLen * r;
    const exitX = curr.x + (next.x - curr.x) / nextLen * r;
    const exitY = curr.y + (next.y - curr.y) / nextLen * r;
    for (let step = 0; step <= CORNER_BEZIER_STEPS; step++) {
      const t = step / CORNER_BEZIER_STEPS;
      const mt = 1 - t;
      path.push({
        x: mt * mt * entryX + 2 * mt * t * curr.x + t * t * exitX,
        y: mt * mt * entryY + 2 * mt * t * curr.y + t * t * exitY
      });
    }
  }
  path.push({ x: path[0].x, y: path[0].y });
  return path;
}
function distanceToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) {
    const distX2 = px - x1;
    const distY2 = py - y1;
    return { distance: Math.sqrt(distX2 * distX2 + distY2 * distY2), t: 0 };
  }
  const t = clamp(((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy), 0, 1);
  const projectedX = x1 + t * dx;
  const projectedY = y1 + t * dy;
  const distX = px - projectedX;
  const distY = py - projectedY;
  return { distance: Math.sqrt(distX * distX + distY * distY), t };
}
function getDevicePowerProfile() {
  const reducedMotion = mediaMatches("(prefers-reduced-motion: reduce)");
  const coarsePointer = mediaMatches("(pointer: coarse)");
  const lowCpu = typeof navigator !== "undefined" && navigator.hardwareConcurrency <= 4;
  const lowMemory = typeof navigator !== "undefined" && "deviceMemory" in navigator && (navigator.deviceMemory ?? 8) <= 4;
  return {
    reducedMotion,
    lowPower: reducedMotion || coarsePointer || lowCpu || lowMemory
  };
}
function buildStaggeredAnchors(rows, cols, insetX, insetY) {
  const anchors = [];
  const cellWidth = (1 - insetX * 2) / cols;
  const rowStep = rows > 1 ? (1 - insetY * 2) / (rows - 1) : 0;
  for (let row = 0; row < rows; row++) {
    const shift = row % 2 === 0 ? 0 : 0.5;
    for (let col = 0; col < cols; col++) {
      const x = insetX + (col + 0.5 + shift) * cellWidth;
      if (x < insetX || x > 1 - insetX) continue;
      const y = insetY + row * rowStep;
      anchors.push([x, y]);
    }
  }
  return anchors;
}
function createPolygons(width, height, lowPower) {
  const isMobile = width < MOBILE_BREAKPOINT;
  const aspect = height / width;
  const baseCols = isMobile ? MOBILE_COLS : DESKTOP_COLS;
  const baseRows = isMobile ? MOBILE_ROWS : DESKTOP_ROWS;
  const insetY = isMobile ? MOBILE_ANCHOR_INSET_Y : DESKTOP_ANCHOR_INSET_Y;
  const scale = Math.max(1, Math.sqrt(aspect));
  const cols = Math.max(baseCols, Math.round(baseCols * scale));
  const rows = Math.max(baseRows, Math.round(baseRows * scale));
  const anchors = buildStaggeredAnchors(rows, cols, ANCHOR_INSET_X, insetY);
  const cellWidth = (1 - ANCHOR_INSET_X * 2) / cols * width;
  const cellHeight = rows > 1 ? (1 - insetY * 2) / (rows - 1) * height : height;
  const cellSize = Math.min(cellWidth, cellHeight);
  const radiusFactor = isMobile ? 0.48 : 0.43;
  const baseRadius = cellSize * radiusFactor;
  const vertices = [];
  for (let i = 0; i < POLYGON_SIDES; i++) {
    const angle = SHARED_ROTATION + Math.PI * 2 / POLYGON_SIDES * i;
    vertices.push({ x: Math.cos(angle) * baseRadius, y: Math.sin(angle) * baseRadius });
  }
  const basePath = buildSmoothPath(vertices);
  return anchors.map(([nx, ny], index) => {
    const depth = index % 3;
    return {
      id: index,
      x: nx * width,
      y: ny * height,
      radius: baseRadius,
      depth,
      rotation: SHARED_ROTATION,
      phase: index * 1.17,
      driftX: lowPower ? 0.15 : 0.22 + depth * 0.05,
      driftY: lowPower ? 0.12 : 0.2 + depth * 0.05,
      basePath
    };
  });
}
function strokeGlowLine(ctx, x1, y1, x2, y2, alpha, coreWidth, r, g, b) {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.08})`;
  ctx.lineWidth = coreWidth + 8;
  ctx.stroke();
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.2})`;
  ctx.lineWidth = coreWidth + 4;
  ctx.stroke();
  ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${alpha * 0.5})`;
  ctx.lineWidth = coreWidth + 1.5;
  ctx.stroke();
  ctx.strokeStyle = `rgba(224, 247, 255, ${clamp(alpha * 0.8, 0.1, 0.9)})`;
  ctx.lineWidth = coreWidth;
  ctx.stroke();
}
function pushUniqueNeighbor(map, key, value) {
  const existing = map.get(key);
  if (!existing) {
    map.set(key, [value]);
    return;
  }
  if (!existing.includes(value)) {
    existing.push(value);
  }
}
function chooseUniqueRandomItems(items, count) {
  if (count <= 0 || items.length === 0) return [];
  if (count >= items.length) return items.slice();
  const result = [];
  const selected = /* @__PURE__ */ new Set();
  while (result.length < count) {
    const index = Math.floor(Math.random() * items.length);
    if (selected.has(index)) continue;
    selected.add(index);
    result.push(items[index]);
  }
  return result;
}
function MeshBackground({ mode = "viewport", className = "" }) {
  const [isReducedMotion, setIsReducedMotion] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e) => setIsReducedMotion(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  const canvasRef = useRef(null);
  const rafIdRef = useRef(0);
  const polygonsRef = useRef([]);
  const neighborsRef = useRef(/* @__PURE__ */ new Map());
  const pathLinksRef = useRef(/* @__PURE__ */ new Map());
  const polygonSegmentIdsRef = useRef(/* @__PURE__ */ new Map());
  const runtimeSegmentsRef = useRef([]);
  const runtimePolygonsRef = useRef([]);
  const pulsesRef = useRef([]);
  const pulseIdRef = useRef(0);
  const lowPowerRef = useRef(false);
  const reducedMotionRef = useRef(false);
  const lastPulseTimestampRef = useRef(0);
  const resonanceRef = useRef(0);
  const resonanceTargetRef = useRef(0);
  const lastRelayTimestampRef = useRef(0);
  const initialPulsesSpawnedRef = useRef(false);
  const initialGlowsRef = useRef([]);
  const dprRef = useRef(1);
  const lastGeometryRebuildRef = useRef(-Infinity);
  const mouseRef = useRef({
    x: -1e3,
    y: -1e3,
    inside: false,
    lastX: -1e3,
    lastY: -1e3,
    lastTime: 0,
    targetSpeed: 0,
    speed: 0,
    targetParallaxX: 0,
    targetParallaxY: 0,
    parallaxX: 0,
    parallaxY: 0,
    moveX: 0,
    moveY: 0
  });
  const rebuildRuntimeGeometry = useCallback((time, width, height) => {
    const mouse = mouseRef.current;
    runtimeSegmentsRef.current = [];
    runtimePolygonsRef.current = [];
    neighborsRef.current = /* @__PURE__ */ new Map();
    pathLinksRef.current = /* @__PURE__ */ new Map();
    polygonSegmentIdsRef.current = /* @__PURE__ */ new Map();
    let segmentId = 0;
    const transformedPolygons = polygonsRef.current.map((polygon) => {
      const depthRatio = polygon.depth === 2 ? 1 : polygon.depth === 1 ? 0.7 : 0.45;
      const driftX = Math.sin(time * 27e-5 + polygon.phase) * polygon.driftX;
      const driftY = Math.cos(time * 23e-5 + polygon.phase * 1.15) * polygon.driftY;
      return {
        ...polygon,
        x: clamp(polygon.x + driftX + mouse.parallaxX * depthRatio, -200, width + 200),
        y: clamp(polygon.y + driftY + mouse.parallaxY * depthRatio, -200, height + 200),
        radius: polygon.radius
      };
    });
    for (const polygon of transformedPolygons) {
      const roundedPath = polygon.basePath.map((p) => ({ x: p.x + polygon.x, y: p.y + polygon.y }));
      runtimePolygonsRef.current.push({
        id: polygon.id,
        depth: polygon.depth,
        roundedPath
      });
      const polygonSegmentIds = [];
      for (let i = 0; i < roundedPath.length - 1; i++) {
        const start = roundedPath[i];
        const end = roundedPath[i + 1];
        const length = distanceBetween(start, end);
        if (length <= 1e-3) continue;
        const id = segmentId++;
        runtimeSegmentsRef.current.push({
          id,
          polygonId: polygon.id,
          x1: start.x,
          y1: start.y,
          x2: end.x,
          y2: end.y,
          length,
          depth: polygon.depth
        });
        neighborsRef.current.set(id, []);
        polygonSegmentIds.push(id);
      }
      if (polygonSegmentIds.length > 1) {
        polygonSegmentIdsRef.current.set(polygon.id, polygonSegmentIds);
        const count = polygonSegmentIds.length;
        for (let i = 0; i < count; i++) {
          const current = polygonSegmentIds[i];
          const backward = polygonSegmentIds[(i - 1 + count) % count];
          const forward = polygonSegmentIds[(i + 1) % count];
          pathLinksRef.current.set(current, { forward, backward });
          pushUniqueNeighbor(neighborsRef.current, current, backward);
          pushUniqueNeighbor(neighborsRef.current, current, forward);
        }
      }
    }
  }, []);
  const spawnPulse = useCallback(
    (segmentId, t, speed, intensityScale = 1, direction = Math.random() > 0.5 ? 1 : -1) => {
      const segment = runtimeSegmentsRef.current[segmentId];
      if (segment) {
        const polygonId = segment.polygonId;
        const alreadyActive = pulsesRef.current.some((p) => {
          const seg = runtimeSegmentsRef.current[p.segmentId];
          return seg && seg.polygonId === polygonId;
        });
        if (alreadyActive) return;
      }
      const normalizedSpeed = clamp(speed, 0, 1);
      const intensity = clamp((0.28 + normalizedSpeed * 0.55) * intensityScale, 0.18, 0.82);
      const pulse = {
        id: pulseIdRef.current++,
        segmentId,
        t: clamp(t, 0, 1),
        direction,
        velocityPerMs: 45e-5 + normalizedSpeed * 105e-5,
        length: 0.09 + normalizedSpeed * 0.08,
        intensity,
        lifeMs: 520 + normalizedSpeed * 280,
        ageMs: 0
      };
      pulsesRef.current.push(pulse);
      if (pulsesRef.current.length > MAX_PULSES) {
        pulsesRef.current.splice(0, pulsesRef.current.length - MAX_PULSES);
      }
    },
    []
  );
  const relayPulseAcrossPolygons = useCallback(
    (originSegmentId, speed, resonance) => {
      if (resonance < RELAY_RESONANCE_THRESHOLD || lowPowerRef.current || reducedMotionRef.current)
        return;
      const now = performance.now();
      const relayCooldown = clamp(
        MAX_RELAY_COOLDOWN_MS - resonance * 55,
        MIN_RELAY_COOLDOWN_MS,
        MAX_RELAY_COOLDOWN_MS
      );
      if (now - lastRelayTimestampRef.current < relayCooldown) return;
      const originSegment = runtimeSegmentsRef.current[originSegmentId];
      if (!originSegment) return;
      const remotePolygonIds = Array.from(polygonSegmentIdsRef.current.keys()).filter(
        (polygonId) => polygonId !== originSegment.polygonId
      );
      if (remotePolygonIds.length === 0) return;
      const relayCount = Math.min(
        Math.floor(resonance * MAX_PULSE_RELAY_PER_EVENT),
        remotePolygonIds.length
      );
      if (relayCount <= 0) return;
      const selectedPolygonIds = chooseUniqueRandomItems(remotePolygonIds, relayCount);
      const relaySpeed = clamp(speed * 0.28 + resonance * 0.16, 0, 0.36);
      const intensityScale = clamp(0.24 + resonance * 0.12, 0.2, 0.34);
      for (const polygonId of selectedPolygonIds) {
        const candidateSegments = polygonSegmentIdsRef.current.get(polygonId) ?? [];
        if (candidateSegments.length === 0) continue;
        const segmentId = candidateSegments[Math.floor(Math.random() * candidateSegments.length)];
        const direction = Math.random() > 0.5 ? 1 : -1;
        spawnPulse(segmentId, Math.random(), relaySpeed, intensityScale, direction);
      }
      lastRelayTimestampRef.current = now;
    },
    [spawnPulse]
  );
  const drawScene = useCallback(
    (ctx, width, height, time, deltaMs) => {
      ctx.clearRect(0, 0, width, height);
      ctx.lineJoin = "round";
      ctx.lineCap = "butt";
      const mouse = mouseRef.current;
      mouse.speed += (mouse.targetSpeed - mouse.speed) * EASING_SMOOTHING;
      mouse.targetSpeed *= 0.93;
      mouse.parallaxX += (mouse.targetParallaxX - mouse.parallaxX) * EASING_SMOOTHING;
      mouse.parallaxY += (mouse.targetParallaxY - mouse.parallaxY) * EASING_SMOOTHING;
      resonanceTargetRef.current *= 0.992;
      const resonanceTarget = clamp(resonanceTargetRef.current + mouse.speed * 0.62, 0, 1);
      resonanceRef.current += (resonanceTarget - resonanceRef.current) * 0.22;
      const resonance = clamp(resonanceRef.current, 0, 1);
      if (time - lastGeometryRebuildRef.current >= 48) {
        rebuildRuntimeGeometry(time, width, height);
        lastGeometryRebuildRef.current = time;
      }
      const breathingLight = 1;
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      for (const polygon of runtimePolygonsRef.current) {
        const baseAlpha = BASE_STROKE_ALPHA[polygon.depth] * breathingLight;
        const baseWidth = BASE_STROKE_WIDTH[polygon.depth];
        const points = polygon.roundedPath;
        if (points.length < 2) continue;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(72, 124, 210, ${baseAlpha})`;
        ctx.lineWidth = baseWidth;
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.stroke();
      }
      ctx.lineCap = "butt";
      ctx.lineJoin = "round";
      let nearestSegmentId = -1;
      let nearestDistance = Number.POSITIVE_INFINITY;
      let nearestT = 0;
      const minDistPerPolygon = /* @__PURE__ */ new Map();
      for (const segment of runtimeSegmentsRef.current) {
        if (!mouse.inside || lowPowerRef.current || reducedMotionRef.current) break;
        const nearest = distanceToSegment(
          mouse.x,
          mouse.y,
          segment.x1,
          segment.y1,
          segment.x2,
          segment.y2
        );
        if (nearest.distance < nearestDistance) {
          nearestDistance = nearest.distance;
          nearestSegmentId = segment.id;
          nearestT = nearest.t;
        }
        const prev = minDistPerPolygon.get(segment.polygonId) ?? Number.POSITIVE_INFINITY;
        if (nearest.distance < prev) minDistPerPolygon.set(segment.polygonId, nearest.distance);
      }
      if (mouse.inside && !lowPowerRef.current && !reducedMotionRef.current) {
        ctx.globalCompositeOperation = "lighter";
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        for (const polygon of runtimePolygonsRef.current) {
          const minDist = minDistPerPolygon.get(polygon.id) ?? Number.POSITIVE_INFINITY;
          const proximity = clamp(1 - minDist / INTERACTION_RADIUS, 0, 1);
          if (proximity <= 0.01) continue;
          const points = polygon.roundedPath;
          if (points.length < 2) continue;
          const baseWidth = BASE_STROKE_WIDTH[polygon.depth];
          const glowAlpha = clamp(proximity * (0.52 + resonance * 0.22), 0, 0.72);
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
          ctx.closePath();
          const gradRadius = INTERACTION_RADIUS * (0.45 + proximity * 0.55);
          const grad = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, gradRadius);
          grad.addColorStop(0, `rgba(44, 192, 255, ${glowAlpha})`);
          grad.addColorStop(0.55, `rgba(44, 160, 240, ${glowAlpha * 0.4})`);
          grad.addColorStop(1, "rgba(34, 100, 200, 0)");
          ctx.strokeStyle = grad;
          ctx.lineWidth = baseWidth + proximity * 6 + resonance * 2;
          ctx.globalAlpha = 0.3;
          ctx.stroke();
          ctx.lineWidth = baseWidth + proximity * 3.5 + resonance * 1;
          ctx.globalAlpha = 0.6;
          ctx.stroke();
          ctx.globalAlpha = 1;
          ctx.lineWidth = baseWidth + proximity * 2.2 + resonance * 0.7;
          ctx.stroke();
          ctx.strokeStyle = `rgba(210, 244, 255, ${clamp(proximity * 0.42 + resonance * 0.1, 0, 0.65)})`;
          ctx.lineWidth = baseWidth * 0.55 + proximity * 0.6 + resonance * 0.2;
          ctx.stroke();
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
      }
      if (nearestSegmentId >= 0 && nearestDistance <= CURSOR_TRAIL_RADIUS && mouse.inside && mouse.speed > 0.04 && !lowPowerRef.current && !reducedMotionRef.current) {
        const segment = runtimeSegmentsRef.current[nearestSegmentId];
        if (segment) {
          const segmentDx = segment.x2 - segment.x1;
          const segmentDy = segment.y2 - segment.y1;
          const dot = segmentDx * mouse.moveX + segmentDy * mouse.moveY;
          const followDirection = dot >= 0 ? 1 : -1;
          const trailLength = CURSOR_TRAIL_MIN_LENGTH + mouse.speed * CURSOR_TRAIL_SPEED_LENGTH;
          const headT = nearestT;
          const tailT = clamp(headT - followDirection * trailLength, 0, 1);
          const head = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, headT);
          const tail = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, tailT);
          const followStrength = clamp(1 - nearestDistance / CURSOR_TRAIL_RADIUS, 0, 1);
          const alpha = clamp(0.12 + followStrength * 0.38 + resonance * 0.1, 0.14, 0.56);
          ctx.globalCompositeOperation = "lighter";
          ctx.lineCap = "round";
          strokeGlowLine(ctx, tail.x, tail.y, head.x, head.y, alpha, 0.75 + followStrength * 0.6 + resonance * 0.15, 56, 189, 248);
          ctx.lineCap = "butt";
          ctx.globalCompositeOperation = "source-over";
        }
      }
      if (nearestSegmentId >= 0 && nearestDistance <= EDGE_TRIGGER_DISTANCE && mouse.inside && mouse.speed > 0.06 && !lowPowerRef.current && !reducedMotionRef.current) {
        const now = performance.now();
        const cooldown = clamp(94 - mouse.speed * 32 - resonance * 18, 42, 94);
        if (now - lastPulseTimestampRef.current >= cooldown) {
          const segment = runtimeSegmentsRef.current[nearestSegmentId];
          const segmentDx = segment ? segment.x2 - segment.x1 : 0;
          const segmentDy = segment ? segment.y2 - segment.y1 : 0;
          const dot = segmentDx * mouse.moveX + segmentDy * mouse.moveY;
          const direction = dot >= 0 ? 1 : -1;
          const intensityScale = 0.72 + resonance * 0.2;
          spawnPulse(nearestSegmentId, nearestT, mouse.speed, intensityScale, direction);
          relayPulseAcrossPolygons(nearestSegmentId, mouse.speed, resonance);
          lastPulseTimestampRef.current = now;
        }
      }
      if (!initialPulsesSpawnedRef.current && !reducedMotionRef.current) {
        initialPulsesSpawnedRef.current = true;
        const allPolygons = [...runtimePolygonsRef.current].sort(() => Math.random() - 0.5);
        const glowCount = Math.max(1, Math.ceil(allPolygons.length * 0.5));
        allPolygons.slice(0, glowCount).forEach((polygon, i) => {
          initialGlowsRef.current.push({
            polygonId: polygon.id,
            // Stagger via progress offset so glows are at different positions
            // from the start — no pre-aging (avoids jump on loop start).
            progress: i / glowCount * 0.8 + Math.random() * 0.15,
            direction: Math.random() > 0.5 ? 1 : -1,
            speed: 6e-5 + Math.random() * 3e-5,
            // Short trail (< 1/6 perimeter) → stays within one edge, no corner wrapping
            trailLength: 0.13 + Math.random() * 0.04,
            intensity: 0.48 + Math.random() * 0.16,
            lifeMs: 2200 + Math.random() * 800,
            ageMs: 0
          });
        });
      }
      const nextPulses = [];
      for (const pulse of pulsesRef.current) {
        const nextAge = pulse.ageMs + deltaMs;
        const normalizedLife = clamp(nextAge / pulse.lifeMs, 0, 1);
        const fade = Math.exp(-normalizedLife * 0.92);
        let nextT = pulse.t + pulse.direction * pulse.velocityPerMs * deltaMs;
        let nextSegmentId = pulse.segmentId;
        let crossedCorners = 0;
        while ((nextT < 0 || nextT > 1) && crossedCorners < 3) {
          const link = pathLinksRef.current.get(nextSegmentId);
          if (!link) break;
          const throughPathId = pulse.direction === 1 ? link.forward : link.backward;
          if (nextT > 1) {
            nextT -= 1;
          } else {
            nextT += 1;
          }
          crossedCorners += 1;
          const branchingCandidates = (neighborsRef.current.get(nextSegmentId) ?? []).filter(
            (candidate) => candidate !== link.forward && candidate !== link.backward
          );
          nextSegmentId = throughPathId;
          const sourceSegment = runtimeSegmentsRef.current[nextSegmentId];
          const crossPolygonCandidates = sourceSegment ? branchingCandidates.filter((candidate) => {
            const candidateSegment = runtimeSegmentsRef.current[candidate];
            return candidateSegment && candidateSegment.polygonId !== sourceSegment.polygonId;
          }) : [];
          const preferredCandidates = crossPolygonCandidates.length > 0 && Math.random() < 0.28 + resonance * 0.16 ? crossPolygonCandidates : branchingCandidates;
          const propagationChance = clamp(
            0.18 + pulse.intensity * 0.2 + resonance * 0.25,
            0.08,
            0.62
          );
          if (preferredCandidates.length > 0 && Math.random() < propagationChance) {
            const candidate = preferredCandidates[Math.floor(Math.random() * preferredCandidates.length)];
            const candidateSegment = runtimeSegmentsRef.current[candidate];
            const sourcePolygonId = sourceSegment?.polygonId;
            const isCrossPolygon = candidateSegment && sourcePolygonId !== void 0 ? candidateSegment.polygonId !== sourcePolygonId : false;
            const propagatedSpeed = clamp(pulse.intensity * 0.68 + resonance * 0.22, 0.18, 0.72);
            const propagatedIntensityScale = isCrossPolygon ? 0.34 + resonance * 0.16 : 0.48 + resonance * 0.2;
            const propagatedDirection = Math.random() < 0.5 ? pulse.direction : pulse.direction === 1 ? -1 : 1;
            spawnPulse(
              candidate,
              Math.random(),
              propagatedSpeed,
              clamp(propagatedIntensityScale, 0.28, 0.68),
              propagatedDirection
            );
          }
        }
        const segment = runtimeSegmentsRef.current[nextSegmentId];
        if (!segment) continue;
        const headT = clamp(nextT, 0, 1);
        const tailT = clamp(headT - pulse.direction * pulse.length, 0, 1);
        const head = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, headT);
        const tail = pointAtT(segment.x1, segment.y1, segment.x2, segment.y2, tailT);
        const cornerDecay = 0.985 ** crossedCorners;
        const nextIntensity = pulse.intensity * cornerDecay;
        const alpha = clamp(nextIntensity * fade * 1.14, 0.08, 1);
        ctx.globalCompositeOperation = "lighter";
        strokeGlowLine(ctx, tail.x, tail.y, head.x, head.y, alpha, 0.95 + alpha * 1.15, 56, 189, 248);
        ctx.globalCompositeOperation = "source-over";
        if (nextAge < pulse.lifeMs) {
          nextPulses.push({
            ...pulse,
            segmentId: nextSegmentId,
            t: headT,
            intensity: nextIntensity,
            ageMs: nextAge
          });
        }
      }
      pulsesRef.current = nextPulses;
      const nextGlows = [];
      for (const glow of initialGlowsRef.current) {
        const nextAge = glow.ageMs + deltaMs;
        if (nextAge >= glow.lifeMs) continue;
        const polygon = runtimePolygonsRef.current.find((p) => p.id === glow.polygonId);
        if (!polygon || polygon.roundedPath.length < 3) continue;
        const path = polygon.roundedPath;
        const n = path.length;
        const nextProgress = glow.progress + glow.direction * glow.speed * deltaMs;
        const life = nextAge / glow.lifeMs;
        const fadeIn = clamp(life / 0.15, 0, 1);
        const fadeOut = clamp((1 - life) / 0.3, 0, 1);
        const fade = fadeIn * fadeOut;
        const alpha = glow.intensity * fade;
        if (alpha < 0.01) {
          nextGlows.push({ ...glow, progress: nextProgress, ageMs: nextAge });
          continue;
        }
        const headIdx = (Math.round(nextProgress * n) % n + n) % n;
        const trailPoints = Math.max(2, Math.round(glow.trailLength * n));
        const points = [];
        for (let i = trailPoints; i >= 0; i--) {
          const idx = glow.direction === 1 ? ((headIdx - i) % n + n) % n : ((headIdx + i) % n + n) % n;
          points.push(path[idx]);
        }
        if (points.length >= 2) {
          ctx.globalCompositeOperation = "lighter";
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(points[0].x, points[0].y);
          for (let i = 1; i < points.length; i++) ctx.lineTo(points[i].x, points[i].y);
          ctx.strokeStyle = `rgba(56, 189, 248, ${alpha * 0.06})`;
          ctx.lineWidth = 6 + alpha * 4;
          ctx.stroke();
          ctx.strokeStyle = `rgba(125, 211, 252, ${alpha * 0.18})`;
          ctx.lineWidth = 3 + alpha * 2;
          ctx.stroke();
          ctx.strokeStyle = `rgba(200, 240, 255, ${alpha * 0.55})`;
          ctx.lineWidth = 1.2 + alpha * 1.2;
          ctx.stroke();
          ctx.lineCap = "butt";
          ctx.globalCompositeOperation = "source-over";
        }
        nextGlows.push({ ...glow, progress: nextProgress, ageMs: nextAge });
      }
      initialGlowsRef.current = nextGlows;
    },
    [rebuildRuntimeGeometry, relayPulseAcrossPolygons, spawnPulse]
  );
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const power = getDevicePowerProfile();
    reducedMotionRef.current = power.reducedMotion;
    lowPowerRef.current = power.lowPower;
    const resetCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = 1;
      dprRef.current = dpr;
      const width = Math.max(1, Math.round(rect.width || window.innerWidth));
      const height = Math.max(1, Math.round(rect.height || window.innerHeight));
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
      const polygons = createPolygons(width, height, lowPowerRef.current);
      polygonsRef.current = polygons;
      neighborsRef.current = /* @__PURE__ */ new Map();
      pathLinksRef.current = /* @__PURE__ */ new Map();
      runtimeSegmentsRef.current = [];
      pulsesRef.current = [];
      initialGlowsRef.current = [];
      resonanceRef.current = 0;
      resonanceTargetRef.current = 0;
      lastRelayTimestampRef.current = 0;
      initialPulsesSpawnedRef.current = false;
    };
    const handleResize = () => {
      resetCanvas();
      drawScene(
        ctx,
        canvas.width / dprRef.current,
        canvas.height / dprRef.current,
        performance.now(),
        16
      );
    };
    const handleMouseMove = (event) => {
      const now = performance.now();
      const mouse = mouseRef.current;
      const rect = canvas.getBoundingClientRect();
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;
      const inside = localX >= 0 && localX <= rect.width && localY >= 0 && localY <= rect.height;
      if (!inside) {
        mouse.inside = false;
        mouse.targetSpeed = 0;
        mouse.targetParallaxX = 0;
        mouse.targetParallaxY = 0;
        return;
      }
      const dx = localX - mouse.lastX;
      const dy = localY - mouse.lastY;
      const dt = Math.max(8, now - mouse.lastTime);
      const velocity = Math.sqrt(dx * dx + dy * dy) / dt;
      const previousMoveX = mouse.moveX;
      const previousMoveY = mouse.moveY;
      const previousMoveMagnitude = Math.hypot(mouse.moveX, mouse.moveY);
      const currentMoveMagnitude = Math.sqrt(dx * dx + dy * dy);
      mouse.x = localX;
      mouse.y = localY;
      mouse.inside = true;
      mouse.lastX = localX;
      mouse.lastY = localY;
      mouse.lastTime = now;
      mouse.moveX = dx;
      mouse.moveY = dy;
      mouse.targetSpeed = Math.max(mouse.targetSpeed, clamp(velocity / 1.15, 0, 1));
      if (previousMoveMagnitude > 1 && currentMoveMagnitude > 1) {
        const previousAngle = Math.atan2(previousMoveY, previousMoveX);
        const currentAngle = Math.atan2(dy, dx);
        const turnRate = Math.abs(normalizeAngleDelta(currentAngle - previousAngle)) / dt;
        const speedSignal = clamp((velocity - CIRCLE_SPEED_THRESHOLD) / 0.8, 0, 1);
        const turnSignal = clamp((turnRate - CIRCLE_TURN_RATE_THRESHOLD) / 0.012, 0, 1);
        const orbitSignal = clamp(speedSignal * (0.45 + turnSignal * 0.9), 0, 1);
        resonanceTargetRef.current = Math.max(resonanceTargetRef.current * 0.94, orbitSignal);
      }
      if (!lowPowerRef.current) {
        const normX = localX / Math.max(1, rect.width) - 0.5;
        const normY = localY / Math.max(1, rect.height) - 0.5;
        mouse.targetParallaxX = normX * MAX_PARALLAX;
        mouse.targetParallaxY = normY * MAX_PARALLAX;
      }
    };
    const handleMouseLeave = () => {
      const mouse = mouseRef.current;
      mouse.inside = false;
      mouse.x = -1e3;
      mouse.y = -1e3;
      mouse.targetSpeed = 0;
      mouse.targetParallaxX = 0;
      mouse.targetParallaxY = 0;
      mouse.moveX = 0;
      mouse.moveY = 0;
      resonanceTargetRef.current = 0;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleMouseLeave);
    window.addEventListener("resize", handleResize);
    resetCanvas();
    drawScene(
      ctx,
      canvas.width / dprRef.current,
      canvas.height / dprRef.current,
      performance.now(),
      16
    );
    if (reducedMotionRef.current) {
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseout", handleMouseLeave);
        window.removeEventListener("resize", handleResize);
      };
    }
    let startTimerId = null;
    let animateFn = null;
    const startAnimationLoop = () => {
      let previousTime = performance.now();
      const animate = (time) => {
        if (document.hidden) return;
        const deltaMs = clamp(time - previousTime, 8, 34);
        previousTime = time;
        drawScene(
          ctx,
          canvas.width / dprRef.current,
          canvas.height / dprRef.current,
          time,
          deltaMs
        );
        rafIdRef.current = window.requestAnimationFrame(animate);
      };
      animateFn = animate;
      rafIdRef.current = window.requestAnimationFrame(animate);
    };
    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.cancelAnimationFrame(rafIdRef.current);
      } else if (animateFn) {
        rafIdRef.current = window.requestAnimationFrame(animateFn);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    startTimerId = setTimeout(() => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(startAnimationLoop, { timeout: 200 });
      } else {
        startAnimationLoop();
      }
    }, 100);
    return () => {
      if (startTimerId) clearTimeout(startTimerId);
      window.cancelAnimationFrame(rafIdRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleMouseLeave);
      window.removeEventListener("resize", handleResize);
    };
  }, [drawScene]);
  if (isReducedMotion) return null;
  const wrapperClassName = (() => {
    const base = mode === "container" ? "absolute inset-0 z-0" : "fixed inset-0 z-0";
    return `${base} ${className}`.trim();
  })();
  const layerClassName = mode === "container" ? "absolute inset-0" : "fixed inset-0";
  return /* @__PURE__ */ jsxs("div", { className: wrapperClassName, style: { backgroundColor: "#03070f" }, children: [
    /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": "ambient-gradients",
        "aria-hidden": "true",
        className: layerClassName,
        style: {
          backgroundImage: "radial-gradient(ellipse at 14% 16%, rgba(20, 48, 114, 0.17) 0%, transparent 60%), radial-gradient(ellipse at 82% 82%, rgba(14, 38, 92, 0.16) 0%, transparent 64%), linear-gradient(180deg, rgba(2,7,16,0.92) 0%, rgba(2,7,18,0.995) 100%)"
        }
      }
    ),
    !isMobile && /* @__PURE__ */ jsx("canvas", { ref: canvasRef, className: `${layerClassName} h-full w-full` }),
    !isMobile && /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": "grain-overlay",
        "aria-hidden": "true",
        className: `${layerClassName} z-[1] pointer-events-none`,
        style: { opacity: 6e-3 },
        children: /* @__PURE__ */ jsxs("svg", { width: "100%", height: "100%", children: [
          /* @__PURE__ */ jsx("filter", { id: "grain-filter", children: /* @__PURE__ */ jsx(
            "feTurbulence",
            {
              type: "fractalNoise",
              baseFrequency: "0.65",
              numOctaves: "3",
              stitchTiles: "stitch"
            }
          ) }),
          /* @__PURE__ */ jsx("rect", { width: "100%", height: "100%", filter: "url(#grain-filter)" })
        ] })
      }
    )
  ] });
}

// src/common/DarkMeshSection.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
function DarkMeshSection({
  children,
  id,
  className = "",
  innerPadding = "px-6 py-10 sm:px-10 sm:py-12 lg:px-12 lg:py-14"
}) {
  return /* @__PURE__ */ jsx2("section", { id, className: `relative py-20 md:py-28 ${className}`, children: /* @__PURE__ */ jsx2("div", { className: "mx-auto w-full max-w-[1540px] px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs2(
    "div",
    {
      "data-nav-theme": "dark",
      className: "relative isolate overflow-hidden rounded-[30px] border border-[#dce6f6]/18 bg-[#0a1628] shadow-[0_26px_90px_rgba(7,18,40,0.45)]",
      children: [
        /* @__PURE__ */ jsx2(MeshBackground, { mode: "container" }),
        /* @__PURE__ */ jsx2(
          "div",
          {
            "aria-hidden": "true",
            className: "pointer-events-none absolute inset-0 z-[1]",
            style: {
              background: "radial-gradient(1200px 680px at 18% 4%, rgba(60,110,210,0.14), transparent 62%), radial-gradient(900px 520px at 82% 22%, rgba(45,102,198,0.14), transparent 66%), linear-gradient(180deg, rgba(6,11,22,0.18) 0%, rgba(7,12,24,0.32) 50%, rgba(7,13,26,0.48) 100%)"
            }
          }
        ),
        /* @__PURE__ */ jsx2("div", { className: `relative z-10 ${innerPadding}`, children })
      ]
    }
  ) }) });
}

// src/common/SeoHead.tsx
import { useContext as useContext2, useEffect as useEffect4 } from "react";
import { useLocation } from "react-router-dom";

// src/config/LandingProvider.tsx
import { createContext, useContext, useEffect as useEffect2 } from "react";
import { jsx as jsx3 } from "react/jsx-runtime";
var BrandConfigContext = createContext(null);
function isTestEnvironment() {
  if (import.meta.env.MODE === "test") return true;
  if (typeof window !== "undefined" && !!window.__vitest__) {
    return true;
  }
  return false;
}
function buildGtmScript(gtmId) {
  const script = document.createElement("script");
  script.setAttribute("data-gtm", gtmId);
  script.textContent = [
    `;(function(w,d,s,l,i){`,
    `w[l]=w[l]||[];`,
    `w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`,
    `var f=d.getElementsByTagName(s)[0],`,
    `j=d.createElement(s),`,
    `dl=l!='dataLayer'?'&l='+l:'';`,
    `j.async=true;`,
    `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;`,
    `f.parentNode.insertBefore(j,f);`,
    `})(window,document,'script','dataLayer','${gtmId}');`
  ].join("");
  return script;
}
function LandingProvider({ config, children }) {
  const gtmId = config.gtm?.id;
  useEffect2(() => {
    if (!gtmId || isTestEnvironment()) return;
    const script = buildGtmScript(gtmId);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [gtmId]);
  return /* @__PURE__ */ jsx3(BrandConfigContext.Provider, { value: config, children });
}
function useBrandConfig() {
  const ctx = useContext(BrandConfigContext);
  if (ctx === null) {
    throw new Error("useBrandConfig must be used within a LandingProvider");
  }
  return ctx;
}

// src/common/StructuredData.tsx
import { useEffect as useEffect3 } from "react";
function StructuredData({ data }) {
  useEffect3(() => {
    const items = Array.isArray(data) ? data : [data];
    const scripts = [];
    for (const item of items) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
      scripts.push(script);
    }
    return () => {
      for (const script of scripts) {
        script.remove();
      }
    };
  }, [data]);
  return null;
}
function organizationSchema(config) {
  const sd = config.structuredData;
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: config.name,
    url: config.baseUrl,
    description: sd.description,
    logo: sd.logo ?? `${config.baseUrl}/favicon.svg`,
    ...sd.contactEmail ? {
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        email: sd.contactEmail,
        availableLanguage: ["German", "English"]
      }
    } : {},
    ...sd.sameAs ? { sameAs: sd.sameAs } : {},
    ...sd.foundingLocation ? {
      foundingLocation: {
        "@type": "Place",
        name: sd.foundingLocation
      }
    } : {},
    ...sd.knowsAbout ? { knowsAbout: sd.knowsAbout } : {}
  };
}
function websiteSchema(config) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: config.name,
    url: config.baseUrl
  };
}
function breadcrumbSchema(baseUrl, items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@id": `${baseUrl}${item.url}`,
        name: item.name
      }
    }))
  };
}

// src/common/SeoHead.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}
function upsertLink(rel, extraAttrs) {
  const selectorParts = [`link[rel="${rel}"]`];
  for (const [k, v] of Object.entries(extraAttrs)) {
    if (k !== "href") {
      selectorParts[0] += `[${k}="${v}"]`;
    }
  }
  let el = document.querySelector(selectorParts[0]);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    for (const [k, v] of Object.entries(extraAttrs)) {
      if (k !== "href") {
        el.setAttribute(k, v);
      }
    }
    document.head.appendChild(el);
  }
  if (extraAttrs.href) {
    el.setAttribute("href", extraAttrs.href);
  }
  return el;
}
function SeoHeadContent() {
  const config = useBrandConfig();
  const location = useLocation();
  useEffect4(() => {
    const pathname = location.pathname;
    const langMatch = pathname.match(/^\/(de|en)(?=\/|$)/);
    const lang = langMatch ? langMatch[1] : "de";
    const pathWithoutLang = pathname.replace(/^\/(de|en)(?=\/|$)/, "") || "/";
    const canonicalPath = lang === "en" ? pathWithoutLang === "/" ? "/en" : `/en${pathWithoutLang}` : pathWithoutLang === "/" ? "/de" : `/de${pathWithoutLang}`;
    const dePath = pathWithoutLang === "/" ? "/de" : `/de${pathWithoutLang}`;
    const enPath = pathWithoutLang === "/" ? "/en" : `/en${pathWithoutLang}`;
    const isLegalPage = /\/(impressum|datenschutz|imprint|privacy)/.test(pathname);
    const isHomepage = /^\/(de|en)\/?$/.test(pathname) || /^\/?$/.test(pathname);
    document.documentElement.lang = lang;
    const ogType = upsertMeta("property", "og:type", "website");
    const ogUrl = upsertMeta("property", "og:url", `${config.baseUrl}${canonicalPath}`);
    const ogLocale = upsertMeta("property", "og:locale", lang === "de" ? "de_DE" : "en_US");
    const ogSiteName = upsertMeta("property", "og:site_name", config.siteName);
    const canonical = upsertLink("canonical", { href: `${config.baseUrl}${canonicalPath}` });
    const hreflangDe = upsertLink("alternate", {
      hreflang: "de",
      href: `${config.baseUrl}${dePath}`
    });
    const hreflangEn = upsertLink("alternate", {
      hreflang: "en",
      href: `${config.baseUrl}${enPath}`
    });
    const hreflangDefault = upsertLink("alternate", {
      hreflang: "x-default",
      href: `${config.baseUrl}${dePath}`
    });
    const scripts = [];
    if (!isLegalPage) {
      const orgScript = document.createElement("script");
      orgScript.type = "application/ld+json";
      orgScript.textContent = JSON.stringify(organizationSchema(config));
      document.head.appendChild(orgScript);
      scripts.push(orgScript);
    }
    if (isHomepage) {
      const websiteScript = document.createElement("script");
      websiteScript.type = "application/ld+json";
      websiteScript.textContent = JSON.stringify(websiteSchema(config));
      document.head.appendChild(websiteScript);
      scripts.push(websiteScript);
    }
    return () => {
      ogType.remove();
      ogUrl.remove();
      ogLocale.remove();
      ogSiteName.remove();
      canonical.remove();
      hreflangDe.remove();
      hreflangEn.remove();
      hreflangDefault.remove();
      scripts.forEach((s) => s.remove());
    };
  }, [config, location.pathname]);
  return null;
}
function SeoHead() {
  const config = useContext2(BrandConfigContext);
  if (!config) return null;
  return /* @__PURE__ */ jsx4(SeoHeadContent, {});
}

// src/common/SmoothScrollLink.tsx
import { useCallback as useCallback2, useMemo } from "react";
import { Link, useLocation as useLocation2 } from "react-router-dom";
import { jsx as jsx5 } from "react/jsx-runtime";
function SmoothScrollLink({
  to,
  children,
  onClick,
  ...props
}) {
  const location = useLocation2();
  const langPrefix = location.pathname.startsWith("/en") ? "/en" : "/de";
  const effectiveTo = useMemo(() => {
    if (to.startsWith("/#")) {
      return `${langPrefix}${to.slice(1)}`;
    }
    return to;
  }, [to, langPrefix]);
  const handleClick = useCallback2(
    (e) => {
      onClick?.(e);
      const isHashLink = to.startsWith("/#") || to.startsWith("#");
      const hash = to.includes("#") ? to.split("#")[1] : null;
      if (isHashLink && hash) {
        const isHomePage = /^\/(de|en)$/.test(location.pathname);
        const isOnSamePage = isHomePage || !to.startsWith("/#") && location.pathname === to.split("#")[0];
        if (isOnSamePage) {
          e.preventDefault();
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
            window.history.pushState(null, "", `#${hash}`);
          }
        }
      }
    },
    [to, location.pathname, onClick]
  );
  return /* @__PURE__ */ jsx5(Link, { to: effectiveTo, onClick: handleClick, ...props, children });
}

// src/hooks/useScrollAnimation.ts
import { useEffect as useEffect5, useRef as useRef2, useState as useState2 } from "react";
function useScrollAnimation(options = {}) {
  const { threshold = 0.1, rootMargin = "0px 0px -50px 0px", triggerOnce = true } = options;
  const ref = useRef2(null);
  const [isVisible, setIsVisible] = useState2(false);
  useEffect5(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);
  return { ref, isVisible };
}

// src/components/AnimatedSection.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
function AnimatedSection({
  children,
  className = "",
  delay = 0
}) {
  const { ref, isVisible } = useScrollAnimation();
  return /* @__PURE__ */ jsx6(
    "div",
    {
      ref,
      className: `transition-all duration-700 ease-out ${className}`,
      style: {
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0px)" : "translateY(30px)",
        transitionDelay: `${delay}ms`
      },
      children
    }
  );
}

// src/components/BentoGrid.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs3 } from "react/jsx-runtime";
var GRADIENT_CLASS = {
  blue: "bg-soft-blue",
  purple: "bg-soft-purple",
  pink: "bg-soft-pink",
  orange: "bg-soft-orange",
  teal: "bg-soft-teal",
  green: "bg-soft-green",
  slate: "bg-[#eef1f6]"
};
var ICON_BG = {
  blue: "bg-blue-100 text-blue-600",
  purple: "bg-purple-100 text-purple-600",
  pink: "bg-pink-100 text-pink-600",
  orange: "bg-orange-100 text-orange-600",
  teal: "bg-teal-100 text-teal-600",
  green: "bg-green-100 text-green-600",
  slate: "bg-slate-200 text-slate-600"
};
function ArrowRight() {
  return /* @__PURE__ */ jsx7("svg", { className: "h-4 w-4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx7("path", { d: "M5 12H19M19 12L12 5M19 12L12 19", strokeLinecap: "round", strokeLinejoin: "round" }) });
}
function BentoGrid({
  label,
  heading,
  headingHighlight,
  description,
  cards,
  id,
  className = ""
}) {
  return /* @__PURE__ */ jsx7("section", { id, "data-nav-theme": "light", className: `bg-[#f2f5fa] py-20 md:py-28 ${className}`, children: /* @__PURE__ */ jsxs3("div", { className: "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", children: [
    (heading || headingHighlight || label) && /* @__PURE__ */ jsx7(AnimatedSection, { children: /* @__PURE__ */ jsxs3("div", { className: "mx-auto mb-14 max-w-3xl text-center", children: [
      label && /* @__PURE__ */ jsx7("p", { className: "section-label", children: label }),
      (heading || headingHighlight) && /* @__PURE__ */ jsxs3("h2", { className: "section-title", children: [
        heading && /* @__PURE__ */ jsxs3(Fragment, { children: [
          heading,
          " "
        ] }),
        headingHighlight && /* @__PURE__ */ jsx7("span", { className: "text-gradient", children: headingHighlight })
      ] }),
      description && /* @__PURE__ */ jsx7("p", { className: "section-subtitle mx-auto", children: description })
    ] }) }),
    /* @__PURE__ */ jsx7("div", { className: "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4", children: cards.map((card, i) => {
      const isLarge = card.size === "large";
      const isWide = card.size === "wide";
      return /* @__PURE__ */ jsx7(
        AnimatedSection,
        {
          delay: i * 80,
          className: `${isLarge ? "lg:col-span-2 lg:row-span-2" : ""} ${isWide ? "lg:col-span-2" : ""}`,
          children: /* @__PURE__ */ jsxs3(
            "div",
            {
              className: `group flex h-full flex-col ${GRADIENT_CLASS[card.color]} rounded-[18px] p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-soft-lg md:p-8 ${isLarge ? "md:p-10" : ""}`,
              children: [
                typeof card.icon === "string" ? /* @__PURE__ */ jsx7("span", { className: `mb-5 ${isLarge ? "text-5xl" : "text-4xl"}`, children: card.icon }) : /* @__PURE__ */ jsx7(
                  "div",
                  {
                    className: `mb-5 flex items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110 ${ICON_BG[card.color]} ${isLarge ? "h-14 w-14" : "h-12 w-12"}`,
                    children: card.icon
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "h3",
                  {
                    className: `mb-3 font-bold text-gray-900 ${isLarge ? "text-2xl md:text-3xl" : "text-[1.25rem]"}`,
                    children: card.title
                  }
                ),
                /* @__PURE__ */ jsx7(
                  "p",
                  {
                    className: `leading-relaxed text-gray-600 ${isLarge ? "text-lg" : "text-[0.94rem]"}`,
                    children: card.description
                  }
                ),
                card.cta && /* @__PURE__ */ jsx7("div", { className: "mt-auto pt-6", children: card.ctaHref ? /* @__PURE__ */ jsxs3(
                  "a",
                  {
                    href: card.ctaHref,
                    className: "inline-flex items-center gap-2 font-semibold text-sky-600 transition-all duration-200 hover:gap-3",
                    children: [
                      card.cta,
                      " ",
                      /* @__PURE__ */ jsx7(ArrowRight, {})
                    ]
                  }
                ) : /* @__PURE__ */ jsxs3("span", { className: "inline-flex items-center gap-2 font-semibold text-sky-600", children: [
                  card.cta,
                  " ",
                  /* @__PURE__ */ jsx7(ArrowRight, {})
                ] }) })
              ]
            }
          )
        },
        card.title
      );
    }) })
  ] }) });
}

// src/components/Button.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
function Button({
  children,
  variant = "primary",
  fullWidth = false,
  className = "",
  ...props
}) {
  const baseStyles = "font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed";
  const variantStyles = {
    primary: "bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl",
    secondary: "bg-white hover:bg-gray-50 text-primary-700 border-2 border-primary-600"
  };
  const widthClass = fullWidth ? "w-full" : "";
  return /* @__PURE__ */ jsx8(
    "button",
    {
      className: `${baseStyles} ${variantStyles[variant]} ${widthClass} ${className}`,
      ...props,
      children
    }
  );
}

// src/components/Container.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
function Container({ children, className = "", maxWidth = "xl" }) {
  const maxWidthClasses = {
    sm: "max-w-screen-sm",
    md: "max-w-screen-md",
    lg: "max-w-screen-lg",
    xl: "max-w-screen-xl",
    "2xl": "max-w-screen-2xl",
    full: "max-w-full"
  };
  return /* @__PURE__ */ jsx9("div", { className: `mx-auto px-4 sm:px-6 lg:px-8 ${maxWidthClasses[maxWidth]} ${className}`, children });
}

// src/components/ContentSection.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function ContentSection({
  children,
  id,
  className = "",
  containerClassName = "",
  maxWidth = "xl",
  background = "white"
}) {
  const backgroundClasses = {
    white: "bg-white",
    gray: "bg-gray-50",
    gradient: "bg-gradient-to-b from-white to-gray-50"
  };
  return /* @__PURE__ */ jsx10("section", { id, className: `py-16 md:py-24 ${backgroundClasses[background]} ${className}`, children: /* @__PURE__ */ jsx10(Container, { maxWidth, className: containerClassName, children }) });
}

// src/components/CTA.tsx
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
function CTA({
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  variant = "default",
  className = ""
}) {
  const variants = {
    default: "bg-muted-50",
    gradient: "bg-gradient-to-r from-primary-500 to-primary-600",
    dark: "bg-gray-900"
  };
  const textColor = variant === "default" ? "text-gray-900" : "text-white";
  const subtitleColor = variant === "default" ? "text-gray-600" : "text-white/80";
  return /* @__PURE__ */ jsx11("section", { className: `py-20 md:py-28 ${variants[variant]} ${className}`, children: /* @__PURE__ */ jsx11("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsxs4("div", { className: "max-w-3xl mx-auto text-center", children: [
    /* @__PURE__ */ jsx11("h2", { className: `text-3xl md:text-4xl lg:text-5xl font-bold mb-4 ${textColor}`, children: title }),
    subtitle && /* @__PURE__ */ jsx11("p", { className: `text-lg mb-8 ${subtitleColor}`, children: subtitle }),
    /* @__PURE__ */ jsxs4("div", { className: "flex flex-col sm:flex-row gap-4 justify-center", children: [
      /* @__PURE__ */ jsxs4(
        "a",
        {
          href: primaryCta.href,
          className: `inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold transition-colors ${variant === "default" ? "bg-primary-500 text-white hover:bg-primary-600" : "bg-white text-primary-600 hover:bg-gray-100"}`,
          children: [
            primaryCta.text,
            /* @__PURE__ */ jsx11(
              "svg",
              {
                className: "w-4 h-4",
                viewBox: "0 0 24 24",
                fill: "none",
                xmlns: "http://www.w3.org/2000/svg",
                children: /* @__PURE__ */ jsx11(
                  "path",
                  {
                    d: "M5 12H19M19 12L12 5M19 12L12 19",
                    stroke: "currentColor",
                    strokeWidth: "2",
                    strokeLinecap: "round",
                    strokeLinejoin: "round"
                  }
                )
              }
            )
          ]
        }
      ),
      secondaryCta && /* @__PURE__ */ jsx11(
        "a",
        {
          href: secondaryCta.href,
          className: `inline-flex items-center justify-center px-8 py-4 rounded-full font-semibold transition-colors ${variant === "default" ? "border border-gray-300 text-gray-700 hover:bg-gray-50" : "border border-white/30 text-white hover:bg-white/10"}`,
          children: secondaryCta.text
        }
      )
    ] })
  ] }) }) });
}

// src/components/DarkCard.tsx
import { jsx as jsx12 } from "react/jsx-runtime";
function DarkCard({ children, className = "" }) {
  return /* @__PURE__ */ jsx12(
    "div",
    {
      className: `df-dark-card relative overflow-hidden rounded-[24px] border border-[var(--df-line-soft)] bg-[linear-gradient(180deg,rgba(17,30,54,0.95)_0%,rgba(11,20,37,0.96)_100%)] p-5 shadow-[var(--df-shadow-card)] sm:p-8 ${className}`,
      children: /* @__PURE__ */ jsx12("div", { className: "relative z-10", children })
    }
  );
}

// src/components/FAQ.tsx
import { jsx as jsx13, jsxs as jsxs5 } from "react/jsx-runtime";
function FAQ({
  label,
  title,
  subtitle,
  items,
  ctaText,
  ctaHref,
  ctaLabel,
  className = ""
}) {
  return /* @__PURE__ */ jsx13("section", { className: `py-20 md:py-28 bg-muted-50 ${className}`, children: /* @__PURE__ */ jsxs5("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs5("div", { className: "text-center max-w-3xl mx-auto mb-16", children: [
      label && /* @__PURE__ */ jsx13("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-4", children: label }),
      /* @__PURE__ */ jsx13("h2", { className: "text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4", children: title }),
      subtitle && /* @__PURE__ */ jsx13("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx13("div", { className: "max-w-4xl mx-auto", children: /* @__PURE__ */ jsx13("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-8", children: items.map((faq, index) => /* @__PURE__ */ jsxs5("div", { className: "bg-white rounded-2xl p-6 shadow-soft", children: [
      /* @__PURE__ */ jsx13("h3", { className: "text-lg font-bold text-gray-900 mb-3", children: faq.question }),
      /* @__PURE__ */ jsx13("p", { className: "text-gray-600 leading-relaxed", children: faq.answer })
    ] }, index)) }) }),
    ctaText && ctaHref && /* @__PURE__ */ jsxs5("div", { className: "text-center mt-12", children: [
      ctaLabel && /* @__PURE__ */ jsx13("p", { className: "text-gray-600 mb-4", children: ctaLabel }),
      /* @__PURE__ */ jsx13(
        "a",
        {
          href: ctaHref,
          className: "inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors",
          children: ctaText
        }
      )
    ] })
  ] }) });
}

// src/components/FeatureCard.tsx
import { jsx as jsx14, jsxs as jsxs6 } from "react/jsx-runtime";
function FeatureCard({ title, items, icon, className = "" }) {
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: `flex min-h-[280px] flex-col justify-between overflow-hidden rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-6 sm:p-8 ${className}`,
      children: [
        /* @__PURE__ */ jsxs6("div", { children: [
          icon && /* @__PURE__ */ jsx14("span", { className: "mb-4 flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-teal-400 to-cyan-400 text-white shadow-[0_2px_8px_rgba(45,212,191,0.3)] sm:h-10 sm:w-10", children: icon }),
          /* @__PURE__ */ jsx14(
            "h3",
            {
              className: "text-[1.25rem] font-semibold leading-[1.2] tracking-tight text-slate-900 sm:text-[1.45rem] break-words hyphens-auto",
              lang: "de",
              children: title
            }
          )
        ] }),
        /* @__PURE__ */ jsx14("ul", { className: "mt-auto space-y-2 pt-6", children: items.map((item) => /* @__PURE__ */ jsxs6("li", { className: "flex items-start gap-2.5 text-[0.94rem] text-slate-700", children: [
          /* @__PURE__ */ jsx14(
            "span",
            {
              "aria-hidden": "true",
              className: "mt-[7px] h-2 w-2 shrink-0 rounded-full bg-teal-400"
            }
          ),
          /* @__PURE__ */ jsx14("span", { children: item })
        ] }, item)) })
      ]
    }
  );
}

// src/components/Features.tsx
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
function Features({
  label,
  title,
  subtitle,
  features,
  columns = 3,
  className = ""
}) {
  const gridCols = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-2 lg:grid-cols-4"
  };
  return /* @__PURE__ */ jsx15("section", { className: `py-20 md:py-28 bg-white ${className}`, children: /* @__PURE__ */ jsxs7("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: [
    /* @__PURE__ */ jsxs7("div", { className: "text-center max-w-3xl mx-auto mb-16", children: [
      label && /* @__PURE__ */ jsx15("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-4", children: label }),
      /* @__PURE__ */ jsx15("h2", { className: "text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4", children: title }),
      subtitle && /* @__PURE__ */ jsx15("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx15("div", { className: `grid grid-cols-1 ${gridCols[columns]} gap-8`, children: features.map((feature, index) => /* @__PURE__ */ jsxs7(
      "div",
      {
        className: "bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-soft-lg transition-shadow duration-300",
        children: [
          /* @__PURE__ */ jsx15("div", { className: "w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mb-6 text-primary-500", children: feature.icon }),
          /* @__PURE__ */ jsx15("h3", { className: "text-xl font-bold text-gray-900 mb-3", children: feature.title }),
          /* @__PURE__ */ jsx15("p", { className: "text-gray-600 leading-relaxed", children: feature.description })
        ]
      },
      index
    )) })
  ] }) });
}

// src/components/Footer.tsx
import { Link as Link2 } from "react-router-dom";
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
function Footer({ copyright, links, attribution, attributionHref }) {
  const attributionClass = "text-sm text-muted transition hover:text-primary";
  return /* @__PURE__ */ jsx16("footer", { className: "border-t border-subtle", children: /* @__PURE__ */ jsx16("div", { className: "mx-auto max-w-7xl px-6 py-8", children: /* @__PURE__ */ jsxs8("div", { className: "flex flex-col items-center justify-between gap-4 md:flex-row", children: [
    /* @__PURE__ */ jsx16("p", { className: "font-mono text-sm text-muted", children: copyright }),
    /* @__PURE__ */ jsxs8("div", { className: "flex flex-wrap items-center gap-4 sm:gap-6", children: [
      links.map(
        (link) => link.external ? /* @__PURE__ */ jsx16(
          "a",
          {
            href: link.href,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-sm text-muted transition hover:text-primary",
            children: link.label
          },
          link.href
        ) : /* @__PURE__ */ jsx16(
          Link2,
          {
            to: link.href,
            className: "text-sm text-muted transition hover:text-primary",
            children: link.label
          },
          link.href
        )
      ),
      attribution && (attributionHref ? /* @__PURE__ */ jsx16(
        "a",
        {
          href: attributionHref,
          target: "_blank",
          rel: "noopener noreferrer",
          className: attributionClass,
          children: attribution
        }
      ) : /* @__PURE__ */ jsx16("span", { className: "text-sm text-muted", children: attribution }))
    ] })
  ] }) }) });
}

// src/components/PageTransition.tsx
import { useRef as useRef3 } from "react";
import { useLocation as useLocation3 } from "react-router-dom";
import { jsx as jsx17 } from "react/jsx-runtime";
function PageTransition({ children }) {
  const location = useLocation3();
  const isFirstRender = useRef3(true);
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return /* @__PURE__ */ jsx17("div", { "data-page-transition": true, children });
  }
  return /* @__PURE__ */ jsx17("div", { "data-page-transition": true, className: "animate-fade-in", children }, location.key);
}

// src/components/ScrollToTop.tsx
import { useEffect as useEffect6 } from "react";
import { useLocation as useLocation4 } from "react-router-dom";
function ScrollToTop() {
  const { hash } = useLocation4();
  useEffect6(() => {
    if (hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [hash]);
  return null;
}

// src/components/Layout.tsx
import { jsx as jsx18, jsxs as jsxs9 } from "react/jsx-runtime";
function Layout({ navbar, footer, seoHead, children }) {
  return /* @__PURE__ */ jsxs9("div", { className: "min-h-screen flex flex-col", children: [
    seoHead ?? /* @__PURE__ */ jsx18(SeoHead, {}),
    /* @__PURE__ */ jsx18(ScrollToTop, {}),
    navbar,
    /* @__PURE__ */ jsx18("main", { className: "relative z-10 flex-grow bg-[#f2f5fa]", children: /* @__PURE__ */ jsx18(PageTransition, { children }) }),
    footer
  ] });
}

// src/components/Navbar.tsx
import { useCallback as useCallback3, useEffect as useEffect7, useRef as useRef4, useState as useState3 } from "react";
import { Link as Link3, useLocation as useLocation5 } from "react-router-dom";
import { Fragment as Fragment2, jsx as jsx19, jsxs as jsxs10 } from "react/jsx-runtime";
function isLanguageLinks(ls) {
  return "dePath" in ls;
}
function isLanguageDropdown(ls) {
  return "options" in ls;
}
function Navbar({
  logo,
  brandName,
  homeLink,
  links,
  dropdown,
  blogPosts,
  blogPostsLabel,
  languageSwitch,
  cta
}) {
  const location = useLocation5();
  const [isMenuOpen, setIsMenuOpen] = useState3(false);
  const [isScrolled, setIsScrolled] = useState3(false);
  const [isLightNav, setIsLightNav] = useState3(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState3(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState3(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState3(false);
  const dropdownTimeoutRef = useRef4(null);
  const dropdownRef = useRef4(null);
  useEffect7(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect7(() => {
    const updateNavTheme = () => {
      const navY = 40;
      const darkSections = document.querySelectorAll("[data-nav-theme='dark']");
      let overDark = false;
      for (const section of darkSections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= navY && rect.bottom >= navY) {
          overDark = true;
          break;
        }
      }
      setIsLightNav(!overDark);
    };
    updateNavTheme();
    window.addEventListener("scroll", updateNavTheme, { passive: true });
    window.addEventListener("resize", updateNavTheme);
    return () => {
      window.removeEventListener("scroll", updateNavTheme);
      window.removeEventListener("resize", updateNavTheme);
    };
  }, []);
  useEffect7(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsMenuOpen(false);
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
  useEffect7(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
    setIsLangDropdownOpen(false);
  }, [location.pathname]);
  const langDropdownRef = useRef4(null);
  useEffect7(() => {
    if (!isLangDropdownOpen) return;
    function handleClick(e) {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isLangDropdownOpen]);
  const closeMobileMenu = useCallback3(() => setIsMenuOpen(false), []);
  const isActivePath = (path) => location.pathname === path || path !== "/" && location.pathname.startsWith(`${path}/`);
  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsDropdownOpen(true);
  };
  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => setIsDropdownOpen(false), 150);
  };
  const dropdownPaths = dropdown?.items.map((i) => i.to) ?? [];
  const isDropdownActive = dropdownPaths.some((p) => isActivePath(p));
  const renderDesktopLink = (link) => {
    const isActive = isActivePath(link.to);
    return /* @__PURE__ */ jsxs10(
      Link3,
      {
        to: link.to,
        className: `inline-flex items-center gap-2 text-[14px] font-medium transition-colors duration-500 ${isLightNav ? isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900" : isActive ? "text-white" : "text-white/90 hover:text-white"}`,
        children: [
          /* @__PURE__ */ jsx19(
            "span",
            {
              "aria-hidden": "true",
              className: `h-1.5 w-1.5 rounded-full transition-all duration-500 ${isLightNav ? isActive ? "bg-sky-500 opacity-100" : "bg-slate-300 opacity-0" : isActive ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
            }
          ),
          /* @__PURE__ */ jsx19("span", { children: link.label })
        ]
      },
      link.to
    );
  };
  const logoWidth = logo?.width ?? 180;
  const renderLogo = (size) => {
    if (!logo) {
      return /* @__PURE__ */ jsx19("span", { className: `font-bold transition-colors duration-500 ${size === "lg" ? "text-xl" : "text-lg"} ${isLightNav ? "text-slate-900" : "text-white"}`, children: brandName });
    }
    if (size === "lg") {
      return /* @__PURE__ */ jsxs10(Fragment2, { children: [
        /* @__PURE__ */ jsx19(
          "img",
          {
            src: logo.light,
            alt: logo.alt,
            className: `absolute inset-0 h-10 w-auto transition-opacity duration-500 ${isLightNav ? "opacity-100" : "opacity-0"}`,
            loading: "eager"
          }
        ),
        /* @__PURE__ */ jsx19(
          "img",
          {
            src: logo.dark,
            alt: "",
            "aria-hidden": "true",
            className: `absolute inset-0 h-10 w-auto transition-opacity duration-500 ${isLightNav ? "opacity-0" : "opacity-100"}`,
            loading: "eager"
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsxs10(Fragment2, { children: [
      /* @__PURE__ */ jsx19(
        "img",
        {
          src: logo.light,
          alt: logo.alt,
          className: `absolute inset-0 h-8 w-auto transition-opacity duration-500 sm:h-9 ${isLightNav ? "opacity-100" : "opacity-0"}`,
          loading: "eager"
        }
      ),
      /* @__PURE__ */ jsx19(
        "img",
        {
          src: logo.dark,
          alt: "",
          "aria-hidden": "true",
          className: `absolute inset-0 h-8 w-auto transition-opacity duration-500 sm:h-9 ${isLightNav ? "opacity-0" : "opacity-100"}`,
          loading: "eager"
        }
      )
    ] });
  };
  const renderDesktopLanguageSwitch = () => {
    if (!languageSwitch) return null;
    if (isLanguageLinks(languageSwitch)) {
      return /* @__PURE__ */ jsxs10(
        "div",
        {
          className: `flex items-center gap-2 border-l pl-3 text-sm transition-colors duration-500 ${isLightNav ? "border-slate-300/30" : "border-sky-100/10"}`,
          children: [
            /* @__PURE__ */ jsx19(
              Link3,
              {
                to: languageSwitch.dePath,
                className: `transition-colors duration-500 ${languageSwitch.current === "de" ? isLightNav ? "font-semibold text-slate-900" : "font-semibold text-white" : isLightNav ? "text-slate-400 hover:text-slate-900" : "text-white/75 hover:text-white"}`,
                children: "DE"
              }
            ),
            /* @__PURE__ */ jsx19("span", { className: `transition-colors duration-500 ${isLightNav ? "text-slate-300" : "text-white/40"}`, children: "|" }),
            /* @__PURE__ */ jsx19(
              Link3,
              {
                to: languageSwitch.enPath,
                className: `transition-colors duration-500 ${languageSwitch.current === "en" ? isLightNav ? "font-semibold text-slate-900" : "font-semibold text-white" : isLightNav ? "text-slate-400 hover:text-slate-900" : "text-white/75 hover:text-white"}`,
                children: "EN"
              }
            )
          ]
        }
      );
    }
    if (isLanguageDropdown(languageSwitch)) {
      return /* @__PURE__ */ jsxs10("div", { ref: langDropdownRef, className: "relative", children: [
        /* @__PURE__ */ jsxs10(
          "button",
          {
            onClick: () => setIsLangDropdownOpen(!isLangDropdownOpen),
            className: `flex items-center gap-1 rounded-lg px-3 py-1.5 text-[13px] font-semibold transition-colors duration-500 ${isLightNav ? "text-slate-500 hover:bg-slate-100 hover:text-slate-900" : "text-white/90 hover:bg-white/10 hover:text-white"}`,
            children: [
              languageSwitch.current.toUpperCase(),
              /* @__PURE__ */ jsx19("svg", { className: "h-3 w-3", viewBox: "0 0 20 20", fill: "currentColor", children: /* @__PURE__ */ jsx19("path", { fillRule: "evenodd", d: "M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z", clipRule: "evenodd" }) })
            ]
          }
        ),
        isLangDropdownOpen && /* @__PURE__ */ jsx19("div", { className: "absolute right-0 top-full z-50 mt-2 min-w-[140px] overflow-hidden rounded-xl border border-[#dce2eb] bg-white py-1 shadow-lg", children: languageSwitch.options.map((opt) => /* @__PURE__ */ jsxs10(
          "button",
          {
            onClick: () => {
              opt.onSelect();
              setIsLangDropdownOpen(false);
            },
            className: `flex w-full items-center gap-2.5 px-4 py-2 text-sm transition-colors ${opt.code === languageSwitch.current ? "bg-sky-50 font-medium text-sky-600" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`,
            children: [
              /* @__PURE__ */ jsx19("span", { className: "font-mono text-xs uppercase", children: opt.code }),
              /* @__PURE__ */ jsx19("span", { children: opt.label })
            ]
          },
          opt.code
        )) })
      ] });
    }
    return /* @__PURE__ */ jsx19(
      "button",
      {
        onClick: languageSwitch.onSwitch,
        className: `text-[13px] font-semibold rounded-lg px-3 py-1.5 transition-colors duration-500 ${isLightNav ? "text-slate-500 hover:text-slate-900 hover:bg-slate-100" : "text-white/90 hover:text-white hover:bg-white/10"}`,
        children: languageSwitch.label
      }
    );
  };
  return /* @__PURE__ */ jsxs10(Fragment2, { children: [
    /* @__PURE__ */ jsx19("nav", { className: "fixed inset-x-0 top-0 z-50 px-3 pb-2 pt-6 sm:px-5 lg:px-8", children: /* @__PURE__ */ jsxs10("div", { className: "mx-auto flex w-full max-w-[1380px] items-center gap-4", children: [
      logo && /* @__PURE__ */ jsx19(
        Link3,
        {
          to: homeLink,
          "aria-label": brandName,
          className: "relative hidden shrink-0 xl:inline-flex",
          style: { height: 40, width: logoWidth },
          children: renderLogo("lg")
        }
      ),
      /* @__PURE__ */ jsx19(
        "div",
        {
          className: `w-full rounded-[999px] px-4 backdrop-blur-2xl transition-all duration-500 sm:px-6 lg:px-7 ${isLightNav ? isScrolled ? "bg-white/65 shadow-[0_8px_32px_rgba(15,23,42,0.08)]" : "bg-white/45 shadow-[0_4px_24px_rgba(15,23,42,0.04)]" : isScrolled ? "bg-[rgba(8,18,38,0.70)] shadow-[0_8px_32px_rgba(1,8,24,0.35)]" : "bg-[rgba(8,18,38,0.55)] shadow-[0_4px_24px_rgba(1,8,24,0.22)]"}`,
          children: /* @__PURE__ */ jsxs10("div", { className: "flex items-center justify-between py-3", children: [
            /* @__PURE__ */ jsx19(
              Link3,
              {
                to: homeLink,
                "aria-label": brandName,
                className: logo ? "relative h-8 w-[150px] sm:h-9 sm:w-[160px] xl:hidden" : void 0,
                children: renderLogo("sm")
              }
            ),
            /* @__PURE__ */ jsxs10("div", { className: "hidden items-center xl:flex xl:w-full", children: [
              /* @__PURE__ */ jsx19("div", { className: "flex items-center gap-5", children: links.map(renderDesktopLink) }),
              /* @__PURE__ */ jsxs10("div", { className: "ml-auto flex items-center gap-5", children: [
                dropdown && /* @__PURE__ */ jsxs10(
                  "div",
                  {
                    ref: dropdownRef,
                    className: "relative flex items-center",
                    onMouseEnter: handleDropdownEnter,
                    onMouseLeave: handleDropdownLeave,
                    children: [
                      /* @__PURE__ */ jsxs10(
                        "button",
                        {
                          type: "button",
                          className: `inline-flex items-center gap-2 text-[14px] font-medium transition-colors duration-500 ${isLightNav ? isDropdownActive || isDropdownOpen ? "text-slate-900" : "text-slate-600 hover:text-slate-900" : isDropdownActive || isDropdownOpen ? "text-white" : "text-white/90 hover:text-white"}`,
                          "aria-expanded": isDropdownOpen,
                          "aria-haspopup": "true",
                          children: [
                            /* @__PURE__ */ jsx19(
                              "span",
                              {
                                "aria-hidden": "true",
                                className: `h-1.5 w-1.5 rounded-full transition-all duration-500 ${isLightNav ? isDropdownActive ? "bg-sky-500 opacity-100" : "bg-slate-300 opacity-0" : isDropdownActive ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
                              }
                            ),
                            /* @__PURE__ */ jsx19("span", { children: dropdown.label }),
                            /* @__PURE__ */ jsx19(
                              "svg",
                              {
                                className: `h-3.5 w-3.5 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`,
                                fill: "none",
                                viewBox: "0 0 24 24",
                                stroke: "currentColor",
                                strokeWidth: 2.5,
                                children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19 9l-7 7-7-7" })
                              }
                            )
                          ]
                        }
                      ),
                      /* @__PURE__ */ jsx19(
                        "div",
                        {
                          className: `absolute right-0 top-full z-50 w-[420px] pt-4 transition-all duration-200 ${isDropdownOpen ? "pointer-events-auto translate-y-0 opacity-100" : "pointer-events-none -translate-y-1 opacity-0"}`,
                          children: /* @__PURE__ */ jsx19(
                            "div",
                            {
                              className: `overflow-hidden rounded-2xl backdrop-blur-2xl transition-colors duration-500 ${isLightNav ? "border border-slate-200/60 bg-white shadow-[0_8px_32px_rgba(15,23,42,0.12)]" : "border border-sky-100/10 bg-[rgba(6,14,32,0.92)] shadow-[0_8px_32px_rgba(1,8,24,0.50)]"}`,
                              children: /* @__PURE__ */ jsxs10("div", { className: "p-5", children: [
                                /* @__PURE__ */ jsx19("p", { className: `mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] ${isLightNav ? "text-slate-400" : "text-sky-100/50"}`, children: dropdown.label }),
                                /* @__PURE__ */ jsx19("div", { className: "space-y-1", children: dropdown.items.map((item) => /* @__PURE__ */ jsxs10(
                                  Link3,
                                  {
                                    to: item.to,
                                    className: `group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${isLightNav ? isActivePath(item.to) ? "bg-slate-100" : "hover:bg-slate-50" : isActivePath(item.to) ? "bg-sky-900/30" : "hover:bg-sky-900/20"}`,
                                    children: [
                                      item.icon && /* @__PURE__ */ jsx19("span", { className: `mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isLightNav ? item.iconColorLight ?? "" : item.iconColorDark ?? ""}`, children: /* @__PURE__ */ jsx19("svg", { className: "h-4 w-4", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", d: item.icon }) }) }),
                                      /* @__PURE__ */ jsxs10("div", { children: [
                                        /* @__PURE__ */ jsx19("p", { className: `text-sm font-semibold ${isLightNav ? "text-slate-800 group-hover:text-slate-900" : "text-sky-50 group-hover:text-white"}`, children: item.label }),
                                        item.description && /* @__PURE__ */ jsx19("p", { className: `text-xs ${isLightNav ? "text-slate-500" : "text-sky-100/50"}`, children: item.description })
                                      ] })
                                    ]
                                  },
                                  item.to
                                )) }),
                                blogPosts && blogPosts.length > 0 && /* @__PURE__ */ jsxs10(Fragment2, { children: [
                                  /* @__PURE__ */ jsx19("div", { className: `my-4 border-t ${isLightNav ? "border-slate-200/60" : "border-sky-100/10"}` }),
                                  blogPostsLabel && /* @__PURE__ */ jsx19("p", { className: `mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${isLightNav ? "text-slate-400" : "text-sky-100/50"}`, children: blogPostsLabel }),
                                  /* @__PURE__ */ jsx19("div", { className: "space-y-0.5", children: blogPosts.map((post) => /* @__PURE__ */ jsxs10(
                                    Link3,
                                    {
                                      to: post.linkTo,
                                      className: `group flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${isLightNav ? "text-slate-600 hover:bg-slate-50 hover:text-slate-900" : "text-sky-100/60 hover:bg-sky-900/20 hover:text-white"}`,
                                      children: [
                                        /* @__PURE__ */ jsx19(
                                          "svg",
                                          {
                                            className: `h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 ${isLightNav ? "text-slate-400" : "text-sky-100/40"}`,
                                            fill: "none",
                                            viewBox: "0 0 24 24",
                                            stroke: "currentColor",
                                            strokeWidth: 2,
                                            children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" })
                                          }
                                        ),
                                        /* @__PURE__ */ jsx19("span", { className: "truncate", children: post.title })
                                      ]
                                    },
                                    post.slug
                                  )) })
                                ] })
                              ] })
                            }
                          )
                        }
                      )
                    ]
                  }
                ),
                renderDesktopLanguageSwitch(),
                /* @__PURE__ */ jsx19(
                  Link3,
                  {
                    to: cta.to,
                    className: `ml-1 inline-flex items-center justify-center rounded-2xl px-5 py-2.5 text-sm font-semibold transition-all duration-500 ${isLightNav ? "bg-slate-900 text-white shadow-[0_4px_16px_rgba(15,23,42,0.18)] hover:bg-slate-800 hover:shadow-[0_8px_24px_rgba(15,23,42,0.22)]" : "bg-white/90 text-slate-900 shadow-[0_4px_16px_rgba(1,8,24,0.25)] hover:-translate-y-[1px] hover:bg-white"}`,
                    children: cta.label
                  }
                )
              ] })
            ] }),
            /* @__PURE__ */ jsx19(
              "button",
              {
                className: "rounded-xl p-2 xl:hidden",
                onClick: () => setIsMenuOpen(true),
                "aria-label": "Open menu",
                children: /* @__PURE__ */ jsx19(
                  "svg",
                  {
                    className: `h-6 w-6 transition-colors duration-500 ${isLightNav ? "text-slate-900" : "text-sky-100"}`,
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor",
                    children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M4 6h16M4 12h16M4 18h16" })
                  }
                )
              }
            )
          ] })
        }
      )
    ] }) }),
    /* @__PURE__ */ jsx19(
      "div",
      {
        className: `fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-sm transition-opacity duration-300 xl:hidden ${isMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"}`,
        onClick: closeMobileMenu,
        "aria-hidden": !isMenuOpen
      }
    ),
    /* @__PURE__ */ jsx19(
      "div",
      {
        "data-testid": "mobile-drawer",
        className: `fixed right-0 top-0 z-50 h-full w-80 max-w-[calc(100vw-2rem)] transform border-l border-sky-100/20 bg-[#08152f]/95 shadow-2xl backdrop-blur-xl transition-transform duration-300 ease-out xl:hidden ${isMenuOpen ? "translate-x-0" : "translate-x-full"}`,
        "aria-hidden": !isMenuOpen,
        children: /* @__PURE__ */ jsxs10("div", { className: "flex h-full flex-col", children: [
          /* @__PURE__ */ jsxs10("div", { className: "flex items-center justify-between border-b border-sky-100/20 p-5", children: [
            logo ? /* @__PURE__ */ jsx19("img", { src: logo.dark, alt: logo.alt, className: "h-7 w-auto", loading: "lazy" }) : /* @__PURE__ */ jsx19("span", { className: "text-lg font-bold text-white", children: brandName }),
            /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-3", children: [
              languageSwitch && isLanguageLinks(languageSwitch) && /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-1.5 text-sm", children: [
                /* @__PURE__ */ jsx19(
                  Link3,
                  {
                    to: languageSwitch.dePath,
                    className: languageSwitch.current === "de" ? "font-semibold text-white" : "text-sky-100/60 hover:text-white",
                    onClick: closeMobileMenu,
                    children: "DE"
                  }
                ),
                /* @__PURE__ */ jsx19("span", { className: "text-sky-100/35", children: "|" }),
                /* @__PURE__ */ jsx19(
                  Link3,
                  {
                    to: languageSwitch.enPath,
                    className: languageSwitch.current === "en" ? "font-semibold text-white" : "text-sky-100/60 hover:text-white",
                    onClick: closeMobileMenu,
                    children: "EN"
                  }
                )
              ] }),
              languageSwitch && isLanguageDropdown(languageSwitch) && /* @__PURE__ */ jsx19("div", { className: "flex items-center gap-1.5 text-sm", children: languageSwitch.options.map((opt, i) => /* @__PURE__ */ jsxs10("span", { className: "flex items-center gap-1.5", children: [
                i > 0 && /* @__PURE__ */ jsx19("span", { className: "text-sky-100/25", children: "|" }),
                /* @__PURE__ */ jsx19(
                  "button",
                  {
                    onClick: () => {
                      opt.onSelect();
                      closeMobileMenu();
                    },
                    className: opt.code === languageSwitch.current ? "font-semibold text-white" : "text-sky-100/60 hover:text-white",
                    children: opt.code.toUpperCase()
                  }
                )
              ] }, opt.code)) }),
              /* @__PURE__ */ jsx19("button", { onClick: closeMobileMenu, className: "rounded-lg p-2", "aria-label": "Close", children: /* @__PURE__ */ jsx19("svg", { className: "h-5 w-5 text-sky-100/90", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M6 18L18 6M6 6l12 12" }) }) })
            ] })
          ] }),
          /* @__PURE__ */ jsx19("div", { className: "flex-1 overflow-y-auto p-5", children: /* @__PURE__ */ jsxs10("div", { className: "space-y-4", children: [
            /* @__PURE__ */ jsx19("div", { className: "space-y-0.5", children: links.map((link) => {
              const isActive = isActivePath(link.to);
              return /* @__PURE__ */ jsxs10(
                Link3,
                {
                  to: link.to,
                  className: `flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors ${isActive ? "bg-sky-900/35 text-white" : "text-sky-100/90 hover:bg-sky-900/35 hover:text-white"}`,
                  onClick: closeMobileMenu,
                  children: [
                    /* @__PURE__ */ jsx19(
                      "span",
                      {
                        "aria-hidden": "true",
                        className: `h-1.5 w-1.5 rounded-full ${isActive ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
                      }
                    ),
                    /* @__PURE__ */ jsx19("span", { children: link.label })
                  ]
                },
                `mobile-${link.to}`
              );
            }) }),
            dropdown && /* @__PURE__ */ jsxs10("div", { children: [
              /* @__PURE__ */ jsxs10(
                "button",
                {
                  type: "button",
                  onClick: () => setIsMobileDropdownOpen(!isMobileDropdownOpen),
                  className: "flex w-full items-center justify-between px-4 pb-1.5",
                  children: [
                    /* @__PURE__ */ jsx19("span", { className: "text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-100/55", children: dropdown.label }),
                    /* @__PURE__ */ jsx19(
                      "svg",
                      {
                        className: `h-3.5 w-3.5 text-sky-100/55 transition-transform duration-200 ${isMobileDropdownOpen ? "rotate-180" : ""}`,
                        fill: "none",
                        viewBox: "0 0 24 24",
                        stroke: "currentColor",
                        strokeWidth: 2.5,
                        children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M19 9l-7 7-7-7" })
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs10("div", { className: `space-y-0.5 overflow-hidden transition-all duration-200 ${isMobileDropdownOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`, children: [
                dropdown.items.map((item) => /* @__PURE__ */ jsxs10(
                  Link3,
                  {
                    to: item.to,
                    className: `flex items-center gap-2 rounded-xl px-4 py-2.5 transition-colors ${isActivePath(item.to) ? "bg-sky-900/35 text-white" : "text-sky-100/90 hover:bg-sky-900/35 hover:text-white"}`,
                    onClick: closeMobileMenu,
                    children: [
                      /* @__PURE__ */ jsx19(
                        "span",
                        {
                          "aria-hidden": "true",
                          className: `h-1.5 w-1.5 rounded-full ${isActivePath(item.to) ? "bg-sky-300 opacity-100" : "bg-sky-200/60 opacity-0"}`
                        }
                      ),
                      /* @__PURE__ */ jsx19("span", { children: item.label })
                    ]
                  },
                  item.to
                )),
                blogPosts && blogPosts.length > 0 && /* @__PURE__ */ jsxs10("div", { className: "mt-2 border-t border-sky-100/10 pt-2", children: [
                  blogPostsLabel && /* @__PURE__ */ jsx19("p", { className: "px-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-100/40", children: blogPostsLabel }),
                  blogPosts.map((post) => /* @__PURE__ */ jsxs10(
                    Link3,
                    {
                      to: post.linkTo,
                      className: "flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-sky-100/70 transition-colors hover:bg-sky-900/35 hover:text-white",
                      onClick: closeMobileMenu,
                      children: [
                        /* @__PURE__ */ jsx19("svg", { className: "h-3 w-3 shrink-0 text-sky-100/40", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: /* @__PURE__ */ jsx19("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" }) }),
                        /* @__PURE__ */ jsx19("span", { className: "truncate", children: post.title })
                      ]
                    },
                    post.slug
                  ))
                ] })
              ] })
            ] })
          ] }) }),
          /* @__PURE__ */ jsxs10("div", { className: "border-t border-sky-100/20 p-6 space-y-3", children: [
            /* @__PURE__ */ jsx19(
              Link3,
              {
                to: cta.to,
                className: "flex w-full items-center justify-center rounded-2xl bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition-all hover:bg-white/90",
                onClick: closeMobileMenu,
                children: cta.label
              }
            ),
            languageSwitch && !isLanguageLinks(languageSwitch) && !isLanguageDropdown(languageSwitch) && /* @__PURE__ */ jsx19(
              "button",
              {
                onClick: () => {
                  languageSwitch.onSwitch();
                  closeMobileMenu();
                },
                className: "flex w-full items-center justify-center rounded-2xl border border-sky-100/20 px-5 py-2.5 text-sm font-medium text-sky-100/90 transition-colors hover:bg-sky-900/35 hover:text-white",
                children: languageSwitch.label
              }
            )
          ] })
        ] })
      }
    )
  ] });
}

// src/components/Hero.tsx
import { jsx as jsx20, jsxs as jsxs11 } from "react/jsx-runtime";
function Hero({
  label,
  title,
  subtitle,
  primaryCta,
  secondaryCta,
  badges,
  trustIndicator,
  illustration,
  className = ""
}) {
  const badgeVariants = {
    blue: "bg-blue-50 text-blue-600 border-blue-200",
    orange: "bg-orange-50 text-orange-600 border-orange-200",
    green: "bg-green-50 text-green-600 border-green-200",
    purple: "bg-purple-50 text-purple-600 border-purple-200"
  };
  return /* @__PURE__ */ jsxs11(
    "section",
    {
      className: `relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20 pt-24 pb-16 ${className}`,
      children: [
        /* @__PURE__ */ jsxs11("div", { className: "absolute inset-0 overflow-hidden pointer-events-none", children: [
          /* @__PURE__ */ jsx20("div", { className: "absolute -top-40 -right-40 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl" }),
          /* @__PURE__ */ jsx20("div", { className: "absolute top-1/2 -left-20 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl" }),
          /* @__PURE__ */ jsx20("div", { className: "absolute bottom-20 right-1/4 w-64 h-64 bg-orange-200/20 rounded-full blur-3xl" })
        ] }),
        /* @__PURE__ */ jsx20("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8 relative z-10", children: /* @__PURE__ */ jsxs11("div", { className: "grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center", children: [
          /* @__PURE__ */ jsxs11("div", { className: "max-w-xl", children: [
            label && /* @__PURE__ */ jsx20("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-6", children: label }),
            /* @__PURE__ */ jsx20("h1", { className: "text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight", children: title }),
            /* @__PURE__ */ jsx20("p", { className: "text-lg md:text-xl text-gray-600 mb-8 leading-relaxed", children: subtitle }),
            badges && badges.length > 0 && /* @__PURE__ */ jsx20("div", { className: "flex flex-wrap gap-3 mb-10", children: badges.map((badge, index) => /* @__PURE__ */ jsxs11(
              "span",
              {
                className: `inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${badgeVariants[badge.variant || "blue"]}`,
                children: [
                  badge.icon,
                  badge.text
                ]
              },
              index
            )) }),
            /* @__PURE__ */ jsxs11("div", { className: "flex flex-col sm:flex-row gap-4", children: [
              primaryCta && /* @__PURE__ */ jsxs11(
                "a",
                {
                  href: primaryCta.href,
                  className: "inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/25",
                  children: [
                    primaryCta.text,
                    /* @__PURE__ */ jsx20(
                      "svg",
                      {
                        className: "w-4 h-4",
                        viewBox: "0 0 24 24",
                        fill: "none",
                        xmlns: "http://www.w3.org/2000/svg",
                        children: /* @__PURE__ */ jsx20(
                          "path",
                          {
                            d: "M5 12H19M19 12L12 5M19 12L12 19",
                            stroke: "currentColor",
                            strokeWidth: "2",
                            strokeLinecap: "round",
                            strokeLinejoin: "round"
                          }
                        )
                      }
                    )
                  ]
                }
              ),
              secondaryCta && /* @__PURE__ */ jsx20(
                "a",
                {
                  href: secondaryCta.href,
                  className: "inline-flex items-center justify-center px-6 py-3.5 rounded-full border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 transition-colors",
                  children: secondaryCta.text
                }
              )
            ] }),
            trustIndicator && /* @__PURE__ */ jsx20("div", { className: "mt-12", children: trustIndicator })
          ] }),
          illustration && /* @__PURE__ */ jsx20("div", { className: "relative hidden lg:block", children: illustration })
        ] }) })
      ]
    }
  );
}

// src/components/LegalPage.tsx
import { Link as Link4 } from "react-router-dom";
import { jsx as jsx21, jsxs as jsxs12 } from "react/jsx-runtime";
function LegalPage({ title, backLabel, backTo, children }) {
  return /* @__PURE__ */ jsx21("section", { className: "pt-24 pb-16 md:py-24", children: /* @__PURE__ */ jsx21(Container, { children: /* @__PURE__ */ jsxs12("div", { className: "max-w-3xl mx-auto", children: [
    /* @__PURE__ */ jsxs12(
      Link4,
      {
        to: backTo,
        className: "inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 mb-8 transition-colors",
        children: [
          /* @__PURE__ */ jsx21(
            "svg",
            {
              className: "w-4 h-4",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx21(
                "path",
                {
                  d: "M19 12H5M5 12L12 19M5 12L12 5",
                  strokeLinecap: "round",
                  strokeLinejoin: "round"
                }
              )
            }
          ),
          backLabel
        ]
      }
    ),
    /* @__PURE__ */ jsx21("h1", { className: "text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-8", children: title }),
    /* @__PURE__ */ jsx21("article", { className: "rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-6 md:p-10 space-y-8", children })
  ] }) }) });
}

// src/components/NotFoundPage.tsx
import { Link as Link5 } from "react-router-dom";
import { jsx as jsx22, jsxs as jsxs13 } from "react/jsx-runtime";
function NotFoundPage({ title, description, backLabel, backTo = "/" }) {
  return /* @__PURE__ */ jsx22("div", { className: "min-h-[70vh] flex items-center justify-center px-4 py-16", children: /* @__PURE__ */ jsxs13("div", { className: "text-center max-w-md mx-auto", children: [
    /* @__PURE__ */ jsx22("p", { className: "text-8xl font-bold text-primary-500 mb-4", children: "404" }),
    /* @__PURE__ */ jsx22("h1", { className: "text-2xl md:text-3xl font-bold text-slate-900 mb-4", children: title }),
    /* @__PURE__ */ jsx22("p", { className: "text-slate-500 mb-8", children: description }),
    /* @__PURE__ */ jsxs13(
      Link5,
      {
        to: backTo,
        className: "inline-flex items-center gap-2 rounded-lg bg-slate-900 py-3 px-6 text-sm font-semibold text-white transition-colors hover:bg-slate-800",
        children: [
          /* @__PURE__ */ jsx22(
            "svg",
            {
              className: "w-4 h-4",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx22("path", { d: "M19 12H5M5 12L12 19M5 12L12 5", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          ),
          backLabel
        ]
      }
    )
  ] }) });
}

// src/components/PageHeroSection.tsx
import { jsx as jsx23, jsxs as jsxs14 } from "react/jsx-runtime";
function PageHeroSection({ label, title, subtitle, className = "" }) {
  return /* @__PURE__ */ jsx23("section", { className: `pt-28 pb-16 md:pt-36 md:pb-24 bg-white ${className}`, children: /* @__PURE__ */ jsx23(Container, { children: /* @__PURE__ */ jsx23(AnimatedSection, { children: /* @__PURE__ */ jsxs14("div", { className: "mx-auto max-w-3xl text-center", children: [
    label && /* @__PURE__ */ jsx23("p", { className: "text-sm font-semibold uppercase tracking-widest text-teal-500 text-center mb-4", children: label }),
    /* @__PURE__ */ jsx23("h1", { className: "text-3xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl", children: title }),
    subtitle && /* @__PURE__ */ jsx23("p", { className: "mt-4 text-lg md:text-xl text-slate-500", children: subtitle })
  ] }) }) }) });
}

// src/components/ContactPage.tsx
import { zodResolver } from "@hookform/resolvers/zod";
import { useState as useState4 } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { jsx as jsx24, jsxs as jsxs15 } from "react/jsx-runtime";
function buildContactSchema(messages) {
  return z.object({
    name: z.string().min(2, messages.nameMin),
    email: z.string().email(messages.emailInvalid),
    company: z.string().optional(),
    message: z.string().min(10, messages.messageMin)
  });
}
var DEFAULT_VALIDATION = {
  nameMin: "Name must be at least 2 characters",
  emailInvalid: "Please enter a valid email address",
  messageMin: "Message must be at least 10 characters"
};
function ContactPage({ labels, endpoint = "/api/contact" }) {
  const contactSchema = buildContactSchema(labels.validation ?? DEFAULT_VALIDATION);
  const [submitStatus, setSubmitStatus] = useState4(
    "idle"
  );
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(contactSchema)
  });
  const onSubmit = async (data) => {
    setSubmitStatus("submitting");
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      window.umami?.track("contact-submit");
      setSubmitStatus("success");
    } catch {
      setSubmitStatus("error");
    }
  };
  return /* @__PURE__ */ jsxs15("div", { children: [
    /* @__PURE__ */ jsx24(PageHeroSection, { title: labels.title, subtitle: labels.subtitle }),
    /* @__PURE__ */ jsx24("section", { className: "pb-16 md:pb-24", children: /* @__PURE__ */ jsx24(Container, { children: /* @__PURE__ */ jsxs15("div", { className: "max-w-2xl mx-auto", children: [
      submitStatus === "success" && /* @__PURE__ */ jsx24(
        "div",
        {
          "data-testid": "contact-success",
          className: "mb-6 rounded-lg bg-teal-50 border border-teal-200 p-4 text-teal-700",
          children: labels.successMessage ?? "Message sent successfully."
        }
      ),
      submitStatus === "error" && /* @__PURE__ */ jsx24(
        "div",
        {
          "data-testid": "contact-error",
          className: "mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700",
          children: labels.errorMessage ?? "Something went wrong."
        }
      ),
      /* @__PURE__ */ jsxs15(
        "form",
        {
          "data-testid": "contact-form",
          onSubmit: handleSubmit(onSubmit),
          className: "space-y-6",
          noValidate: true,
          children: [
            /* @__PURE__ */ jsxs15("div", { children: [
              /* @__PURE__ */ jsx24("label", { htmlFor: "contact-name", className: "block text-sm font-medium text-slate-700", children: labels.nameLabel }),
              /* @__PURE__ */ jsx24(
                "input",
                {
                  id: "contact-name",
                  type: "text",
                  ...register("name"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              ),
              errors.name && /* @__PURE__ */ jsx24("p", { className: "mt-1 text-sm text-red-600", children: errors.name.message })
            ] }),
            /* @__PURE__ */ jsxs15("div", { children: [
              /* @__PURE__ */ jsx24("label", { htmlFor: "contact-email", className: "block text-sm font-medium text-slate-700", children: labels.emailLabel }),
              /* @__PURE__ */ jsx24(
                "input",
                {
                  id: "contact-email",
                  type: "email",
                  ...register("email"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              ),
              errors.email && /* @__PURE__ */ jsx24("p", { className: "mt-1 text-sm text-red-600", children: errors.email.message })
            ] }),
            /* @__PURE__ */ jsxs15("div", { children: [
              /* @__PURE__ */ jsx24(
                "label",
                {
                  htmlFor: "contact-company",
                  className: "block text-sm font-medium text-slate-700",
                  children: labels.companyLabel ?? "Company"
                }
              ),
              /* @__PURE__ */ jsx24(
                "input",
                {
                  id: "contact-company",
                  type: "text",
                  ...register("company"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs15("div", { children: [
              /* @__PURE__ */ jsx24(
                "label",
                {
                  htmlFor: "contact-message",
                  className: "block text-sm font-medium text-slate-700",
                  children: labels.messageLabel
                }
              ),
              /* @__PURE__ */ jsx24(
                "textarea",
                {
                  id: "contact-message",
                  rows: 5,
                  ...register("message"),
                  className: "mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-slate-900 focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                }
              ),
              errors.message && /* @__PURE__ */ jsx24("p", { className: "mt-1 text-sm text-red-600", children: errors.message.message })
            ] }),
            /* @__PURE__ */ jsx24(
              "button",
              {
                type: "submit",
                disabled: submitStatus === "submitting",
                className: "w-full rounded-lg bg-slate-900 py-3 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed",
                children: labels.submitButton
              }
            )
          ]
        }
      )
    ] }) }) })
  ] });
}

// src/components/ThankYouPage.tsx
import { useEffect as useEffect8 } from "react";
import { Link as Link6 } from "react-router-dom";
import { jsx as jsx25, jsxs as jsxs16 } from "react/jsx-runtime";
function ThankYouPage({
  labels,
  homePath,
  nextStepUrl,
  onMount,
  onCtaClick
}) {
  useEffect8(() => {
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => robots.remove();
  }, []);
  useEffect8(() => {
    onMount?.();
  }, []);
  const showNextStep = Boolean(labels.nextStep && nextStepUrl);
  return /* @__PURE__ */ jsx25("section", { className: "bg-[#f2f5fa] pt-20 pb-24 min-h-screen", children: /* @__PURE__ */ jsx25(Container, { children: /* @__PURE__ */ jsx25("div", { className: "max-w-2xl mx-auto", children: /* @__PURE__ */ jsxs16("div", { className: "rounded-[18px] border border-[#dce2eb] bg-white p-8 md:p-12 text-center", children: [
    /* @__PURE__ */ jsx25("div", { className: "w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6", children: /* @__PURE__ */ jsx25(
      "svg",
      {
        className: "w-8 h-8 text-green-600",
        viewBox: "0 0 24 24",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "2.5",
        children: /* @__PURE__ */ jsx25("path", { d: "M20 6L9 17L4 12", strokeLinecap: "round", strokeLinejoin: "round" })
      }
    ) }),
    /* @__PURE__ */ jsx25("h1", { className: "text-3xl md:text-4xl font-semibold text-gray-900 mb-4", children: labels.title }),
    /* @__PURE__ */ jsx25("p", { className: "text-gray-600 mb-10", children: labels.subtitle }),
    showNextStep && labels.nextStep && nextStepUrl && /* @__PURE__ */ jsxs16("div", { className: "bg-slate-900 rounded-[18px] p-6 text-white text-left mb-8", children: [
      /* @__PURE__ */ jsx25("h2", { className: "font-semibold text-lg mb-2", children: labels.nextStep.heading }),
      /* @__PURE__ */ jsx25("p", { className: "text-sm text-white/80 mb-4", children: labels.nextStep.description }),
      /* @__PURE__ */ jsxs16(
        "a",
        {
          href: nextStepUrl,
          target: "_blank",
          rel: "noopener noreferrer",
          onClick: onCtaClick,
          className: "inline-flex items-center gap-2 bg-white text-slate-900 px-5 py-2.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors w-full justify-center",
          children: [
            labels.nextStep.cta,
            /* @__PURE__ */ jsx25(
              "svg",
              {
                className: "w-4 h-4",
                viewBox: "0 0 24 24",
                fill: "none",
                stroke: "currentColor",
                strokeWidth: "2",
                children: /* @__PURE__ */ jsx25(
                  "path",
                  {
                    d: "M5 12H19M19 12L12 5M19 12L12 19",
                    strokeLinecap: "round",
                    strokeLinejoin: "round"
                  }
                )
              }
            )
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs16(
      Link6,
      {
        to: homePath,
        className: "text-sm text-gray-500 hover:text-gray-900 transition-colors",
        children: [
          "\u2190 ",
          labels.backHome
        ]
      }
    )
  ] }) }) }) });
}

// src/components/WorksWithSection.tsx
import { jsx as jsx26, jsxs as jsxs17 } from "react/jsx-runtime";
function WorksWithSection({
  label,
  heading,
  headingHighlight,
  description,
  agents,
  cards
}) {
  return /* @__PURE__ */ jsxs17(
    "section",
    {
      id: "works-with",
      "data-nav-theme": "light",
      className: "relative z-20 bg-[#f2f5fa] pb-20 pt-20 md:pb-28 md:pt-28",
      children: [
        /* @__PURE__ */ jsx26("div", { className: "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsx26(AnimatedSection, { children: /* @__PURE__ */ jsxs17("div", { className: "mx-auto mb-6 max-w-3xl text-center", children: [
          label && /* @__PURE__ */ jsx26("p", { className: "mb-4 text-sm font-semibold uppercase tracking-[0.2em] text-sky-500", children: label }),
          /* @__PURE__ */ jsxs17("h2", { className: "font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl", children: [
            heading,
            " ",
            headingHighlight && /* @__PURE__ */ jsx26("span", { className: "bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent", children: headingHighlight })
          ] }),
          description && /* @__PURE__ */ jsx26("p", { className: "mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-600", children: description })
        ] }) }) }),
        /* @__PURE__ */ jsx26(AnimatedSection, { delay: 160, children: /* @__PURE__ */ jsx26("div", { className: "px-4 sm:px-6 lg:px-10", children: /* @__PURE__ */ jsx26(
          "div",
          {
            "data-testid": "works-with-agent-grid",
            className: "grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5",
            children: agents.map((agent, i) => /* @__PURE__ */ jsx26(
              "div",
              {
                className: "relative flex h-[76px] items-center justify-center rounded-xl border border-[#dce3ed] bg-[#e8edf4] px-6 text-sm font-semibold text-slate-600 transition-all duration-200 hover:border-[#c8d1de] hover:bg-[#dfe6ef] hover:text-slate-900",
                children: agent.name
              },
              `${agent.name}-${i}`
            ))
          }
        ) }) }),
        cards && cards.length > 0 && /* @__PURE__ */ jsx26("div", { className: "mx-auto max-w-7xl px-4 sm:px-6 lg:px-8", children: /* @__PURE__ */ jsx26("div", { className: "mt-20 grid grid-cols-1 gap-6 lg:grid-cols-3", children: cards.map((card, index) => /* @__PURE__ */ jsx26(AnimatedSection, { delay: (index + 2) * 120, children: /* @__PURE__ */ jsxs17("div", { className: "flex h-full flex-col rounded-[18px] border border-[#dce2eb] bg-[#eef1f6] p-8", children: [
          /* @__PURE__ */ jsx26("span", { className: "mb-4 text-3xl", children: card.icon }),
          /* @__PURE__ */ jsx26("h3", { className: "mb-2 text-lg font-semibold text-slate-900", children: card.title }),
          /* @__PURE__ */ jsx26("p", { className: "text-[0.94rem] leading-relaxed text-slate-700", children: card.description })
        ] }) }, card.title)) }) })
      ]
    }
  );
}

// src/components/PageLoadingIndicator.tsx
import { jsx as jsx27 } from "react/jsx-runtime";
function PageLoadingIndicator() {
  return /* @__PURE__ */ jsx27(
    "div",
    {
      role: "status",
      "aria-label": "Loading page",
      className: "flex justify-center items-center min-h-[60vh] py-20",
      children: /* @__PURE__ */ jsx27("div", { className: "animate-spin w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full" })
    }
  );
}

// src/components/Pricing.tsx
import { jsx as jsx28, jsxs as jsxs18 } from "react/jsx-runtime";
function Pricing({
  label,
  title,
  tiers,
  popularBadge = "Most Popular",
  gridCols,
  className = ""
}) {
  const cols = gridCols ?? (tiers.length <= 3 ? 3 : 4);
  const gridClass = cols === 4 ? "grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4 items-stretch" : "grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 items-stretch";
  return /* @__PURE__ */ jsxs18("div", { className, children: [
    /* @__PURE__ */ jsx28("section", { "data-testid": "pricing-hero", className: "pt-28 pb-16 md:pt-36 md:pb-24 bg-white", children: /* @__PURE__ */ jsx28(Container, { children: /* @__PURE__ */ jsxs18(AnimatedSection, { children: [
      label && /* @__PURE__ */ jsx28("p", { className: "text-sm font-semibold uppercase tracking-widest text-teal-500 text-center mb-4", children: label }),
      /* @__PURE__ */ jsx28("h1", { className: "text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 text-center", children: title })
    ] }) }) }),
    /* @__PURE__ */ jsx28("section", { className: "pb-16 md:pb-24", children: /* @__PURE__ */ jsx28(Container, { children: /* @__PURE__ */ jsx28(AnimatedSection, { children: /* @__PURE__ */ jsx28("div", { "data-testid": "pricing-grid", className: gridClass, children: tiers.map((tier) => /* @__PURE__ */ jsxs18(
      "div",
      {
        "data-testid": `pricing-card-${tier.name.toLowerCase()}`,
        className: `relative flex h-full flex-col rounded-2xl border p-6 sm:p-8 ${tier.popular ? "border-sky-500 ring-2 ring-sky-500 shadow-xl bg-slate-900 text-white" : "border-slate-200 bg-white shadow-sm"}`,
        children: [
          tier.popular && /* @__PURE__ */ jsx28(
            "span",
            {
              "data-testid": "pricing-popular-badge",
              className: "absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-sky-500 px-4 py-1 text-sm font-semibold text-white",
              children: popularBadge
            }
          ),
          /* @__PURE__ */ jsx28("h2", { className: `text-xl font-bold ${tier.popular ? "text-white" : "text-slate-900"}`, children: tier.name }),
          /* @__PURE__ */ jsxs18("div", { className: "mt-4 flex items-baseline gap-1", children: [
            /* @__PURE__ */ jsx28("span", { className: `text-4xl md:text-5xl font-bold ${tier.popular ? "text-white" : "text-slate-900"}`, children: tier.price }),
            tier.period && /* @__PURE__ */ jsx28("span", { className: `text-base ${tier.popular ? "text-slate-300" : "text-slate-500"}`, children: tier.period })
          ] }),
          tier.subtitle && /* @__PURE__ */ jsx28("p", { className: `mt-2 text-sm ${tier.popular ? "text-slate-300" : "text-slate-600"}`, children: tier.subtitle }),
          /* @__PURE__ */ jsx28("ul", { className: "mt-6 mb-8 flex-grow space-y-3", children: tier.features.map((feature) => /* @__PURE__ */ jsxs18("li", { className: "flex items-start gap-2", children: [
            /* @__PURE__ */ jsx28(
              "svg",
              {
                className: `h-5 w-5 flex-shrink-0 ${tier.popular ? "text-sky-300" : "text-teal-500"}`,
                fill: "none",
                viewBox: "0 0 24 24",
                strokeWidth: 2,
                stroke: "currentColor",
                children: /* @__PURE__ */ jsx28("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M4.5 12.75l6 6 9-13.5" })
              }
            ),
            /* @__PURE__ */ jsx28("span", { className: `text-sm ${tier.popular ? "text-slate-200" : "text-slate-600"}`, children: feature })
          ] }, feature)) }),
          /* @__PURE__ */ jsx28(
            "a",
            {
              href: tier.ctaHref ?? "#",
              className: `block w-full rounded-lg py-3 px-4 text-center text-sm font-semibold transition-colors ${tier.popular ? "bg-sky-500 text-white hover:bg-sky-600" : "bg-slate-900 text-white hover:bg-slate-800"}`,
              children: tier.cta
            }
          )
        ]
      },
      tier.name
    )) }) }) }) })
  ] });
}

// src/components/Section.tsx
import { jsx as jsx29, jsxs as jsxs19 } from "react/jsx-runtime";
function Section({
  children,
  id,
  className = "",
  background = "white",
  padding = "lg"
}) {
  const backgrounds = {
    white: "bg-white",
    muted: "bg-muted-50",
    gradient: "bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20"
  };
  const paddings = {
    sm: "py-12 md:py-16",
    md: "py-16 md:py-20",
    lg: "py-20 md:py-28"
  };
  return /* @__PURE__ */ jsx29("section", { id, className: `${backgrounds[background]} ${paddings[padding]} ${className}`, children: /* @__PURE__ */ jsx29("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children }) });
}
function SectionHeader({
  label,
  title,
  subtitle,
  align = "center",
  className = ""
}) {
  const alignClasses = {
    left: "text-left",
    center: "text-center max-w-3xl mx-auto"
  };
  return /* @__PURE__ */ jsxs19("div", { className: `mb-16 ${alignClasses[align]} ${className}`, children: [
    label && /* @__PURE__ */ jsx29("p", { className: "inline-flex items-center px-4 py-1.5 rounded-full bg-primary-50 text-primary-600 text-sm font-medium border border-primary-200 mb-4", children: label }),
    /* @__PURE__ */ jsx29("h2", { className: "text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4", children: title }),
    subtitle && /* @__PURE__ */ jsx29("p", { className: "text-lg text-gray-600 max-w-2xl mx-auto", children: subtitle })
  ] });
}

// src/components/sections/CTASection.tsx
import { Fragment as Fragment3, jsx as jsx30, jsxs as jsxs20 } from "react/jsx-runtime";
function CTASection({ headline, headlineHighlight, button, href, card }) {
  return /* @__PURE__ */ jsx30("section", { className: "pb-12 pt-24 md:pb-16 md:pt-32", children: /* @__PURE__ */ jsx30("div", { className: "px-6 sm:px-10 lg:px-16", children: /* @__PURE__ */ jsxs20("div", { className: "flex flex-col items-start justify-between gap-12 lg:flex-row lg:items-end lg:gap-16", children: [
    /* @__PURE__ */ jsxs20("div", { className: "flex-shrink-0", children: [
      /* @__PURE__ */ jsxs20("h2", { className: "font-display text-4xl font-bold tracking-tight text-slate-900 md:text-5xl lg:text-6xl", children: [
        headline.split("\n").map((line, i) => /* @__PURE__ */ jsxs20("span", { children: [
          i > 0 && /* @__PURE__ */ jsx30("br", {}),
          line
        ] }, i)),
        headlineHighlight && /* @__PURE__ */ jsxs20(Fragment3, { children: [
          " ",
          /* @__PURE__ */ jsx30("br", {}),
          /* @__PURE__ */ jsx30(
            "span",
            {
              "data-highlight": true,
              className: "bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent",
              children: headlineHighlight
            }
          )
        ] })
      ] }),
      /* @__PURE__ */ jsx30("div", { className: "mt-8", children: /* @__PURE__ */ jsx30(
        "a",
        {
          href,
          className: "inline-flex items-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:bg-slate-800 hover:shadow-xl",
          children: button
        }
      ) })
    ] }),
    card && /* @__PURE__ */ jsxs20("div", { className: "w-full max-w-md rounded-2xl bg-[#e8edf4] p-8 lg:p-10", children: [
      /* @__PURE__ */ jsx30("h3", { className: "text-xl font-bold tracking-tight text-slate-900", children: card.title }),
      /* @__PURE__ */ jsx30("p", { className: "mt-3 text-sm leading-relaxed text-slate-600", children: card.description }),
      /* @__PURE__ */ jsx30(
        "a",
        {
          href: card.href,
          className: "mt-5 inline-block text-sm font-medium text-slate-900 underline decoration-slate-900/40 underline-offset-4 transition-colors hover:decoration-slate-900",
          children: card.link
        }
      )
    ] })
  ] }) }) });
}

// src/components/sections/Testimonials.tsx
import { useRef as useRef5, useState as useState5 } from "react";
import { jsx as jsx31, jsxs as jsxs21 } from "react/jsx-runtime";
var INITIAL_COUNT = 4;
function Testimonials({
  title,
  subtitle,
  items,
  showMoreLabel,
  showLessLabel,
  prevLabel = "Previous",
  nextLabel = "Next"
}) {
  const scrollRef = useRef5(null);
  const [showAll, setShowAll] = useState5(false);
  const visibleItems = showAll ? items : items.slice(0, INITIAL_COUNT);
  const canExpand = showMoreLabel && showLessLabel && items.length > INITIAL_COUNT;
  function scrollCarousel(direction) {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === "next" ? scrollAmount : -scrollAmount,
      behavior: "smooth"
    });
  }
  return /* @__PURE__ */ jsx31("section", { id: "testimonials", className: "py-20 md:py-28 bg-dark-900", children: /* @__PURE__ */ jsxs21("div", { className: "container mx-auto px-4 sm:px-6 lg:px-8", children: [
    (title || subtitle) && /* @__PURE__ */ jsxs21("div", { className: "text-center max-w-3xl mx-auto mb-16", children: [
      title && /* @__PURE__ */ jsx31("h2", { className: "text-4xl md:text-5xl font-bold text-white mb-6", children: title }),
      subtitle && /* @__PURE__ */ jsx31("p", { className: "text-lg text-gray-400", children: subtitle })
    ] }),
    /* @__PURE__ */ jsx31(
      "div",
      {
        ref: scrollRef,
        className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto snap-x snap-mandatory md:overflow-x-visible md:snap-none",
        children: visibleItems.map((testimonial, index) => /* @__PURE__ */ jsx31("div", { className: "snap-start min-w-[85vw] md:min-w-0", children: /* @__PURE__ */ jsx31(TestimonialCard, { testimonial }) }, index))
      }
    ),
    /* @__PURE__ */ jsxs21("div", { className: "flex justify-center gap-4 mt-6 md:hidden", children: [
      /* @__PURE__ */ jsx31(
        "button",
        {
          "aria-label": prevLabel,
          onClick: () => scrollCarousel("prev"),
          className: "w-10 h-10 rounded-full bg-dark-800 border border-dark-700 flex items-center justify-center text-white hover:bg-dark-700 transition-colors",
          children: /* @__PURE__ */ jsx31(
            "svg",
            {
              className: "w-5 h-5",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx31("path", { d: "M15 19L8 12L15 5", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          )
        }
      ),
      /* @__PURE__ */ jsx31(
        "button",
        {
          "aria-label": nextLabel,
          onClick: () => scrollCarousel("next"),
          className: "w-10 h-10 rounded-full bg-dark-800 border border-dark-700 flex items-center justify-center text-white hover:bg-dark-700 transition-colors",
          children: /* @__PURE__ */ jsx31(
            "svg",
            {
              className: "w-5 h-5",
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx31("path", { d: "M9 5L16 12L9 19", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          )
        }
      )
    ] }),
    canExpand && /* @__PURE__ */ jsx31("div", { className: "text-center mt-12", children: /* @__PURE__ */ jsxs21(
      "button",
      {
        onClick: () => setShowAll(!showAll),
        className: "btn-secondary bg-transparent border-gray-600 text-white hover:bg-dark-800 inline-flex items-center gap-2",
        children: [
          showAll ? showLessLabel : showMoreLabel,
          /* @__PURE__ */ jsx31(
            "svg",
            {
              className: `w-4 h-4 transition-transform duration-300 ${showAll ? "rotate-180" : ""}`,
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              strokeWidth: "2",
              children: /* @__PURE__ */ jsx31("path", { d: "M6 9L12 15L18 9", strokeLinecap: "round", strokeLinejoin: "round" })
            }
          )
        ]
      }
    ) })
  ] }) });
}
function TestimonialCard({ testimonial }) {
  return /* @__PURE__ */ jsxs21("div", { className: "bg-dark-800 rounded-3xl p-8 border border-dark-700 hover:border-dark-600 transition-all duration-300 group h-full flex flex-col", children: [
    /* @__PURE__ */ jsxs21("p", { className: "text-gray-300 mb-8 leading-relaxed flex-grow", children: [
      "\u201C",
      testimonial.quote,
      "\u201D"
    ] }),
    /* @__PURE__ */ jsxs21("div", { className: "flex items-center gap-4 mt-auto", children: [
      testimonial.avatar && /* @__PURE__ */ jsx31("div", { className: "w-12 h-12 rounded-xl overflow-hidden flex-shrink-0", children: /* @__PURE__ */ jsx31(
        "img",
        {
          src: testimonial.avatar,
          alt: testimonial.name,
          className: "w-full h-full object-cover"
        }
      ) }),
      /* @__PURE__ */ jsxs21("div", { children: [
        /* @__PURE__ */ jsx31("p", { className: "font-semibold text-white", children: testimonial.name }),
        /* @__PURE__ */ jsxs21("p", { className: "text-sm text-gray-500", children: [
          testimonial.role,
          testimonial.company ? `, ${testimonial.company}` : ""
        ] })
      ] })
    ] })
  ] });
}

// src/hooks/useDocumentTitle.ts
import { useContext as useContext3, useEffect as useEffect9 } from "react";
function upsertMeta2(attr, key, content) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return el;
}
function useDocumentTitle(options) {
  const config = useContext3(BrandConfigContext);
  useEffect9(() => {
    if (!config) return;
    const opts = typeof options === "string" ? { title: options } : options ?? {};
    const { title, description, breadcrumbs } = opts;
    const fullTitle = title ? `${title} | ${config.siteName}` : config.siteName;
    document.title = fullTitle;
    const metaDesc = description ? upsertMeta2("name", "description", description) : null;
    const ogTitle = upsertMeta2("property", "og:title", fullTitle);
    const ogDesc = description ? upsertMeta2("property", "og:description", description) : null;
    let crumbScript = null;
    if (breadcrumbs && breadcrumbs.length > 0) {
      crumbScript = document.createElement("script");
      crumbScript.type = "application/ld+json";
      crumbScript.textContent = JSON.stringify(breadcrumbSchema(config.baseUrl, breadcrumbs));
      document.head.appendChild(crumbScript);
    }
    return () => {
      document.title = config.siteName;
      metaDesc?.remove();
      ogTitle.remove();
      ogDesc?.remove();
      crumbScript?.remove();
    };
  }, [config, options]);
}
export {
  AnimatedSection,
  BentoGrid,
  Button,
  CTA,
  CTASection,
  ContactPage,
  Container,
  ContentSection,
  DarkCard,
  DarkMeshSection,
  FAQ,
  FeatureCard,
  Features,
  Footer,
  Hero,
  LandingProvider,
  Layout,
  LegalPage,
  MeshBackground,
  Navbar,
  NotFoundPage,
  PageHeroSection,
  PageLoadingIndicator,
  PageTransition,
  Pricing,
  ScrollToTop,
  Section,
  SectionHeader,
  SeoHead,
  SmoothScrollLink,
  StructuredData,
  Testimonials,
  ThankYouPage,
  WorksWithSection,
  breadcrumbSchema,
  organizationSchema,
  useBrandConfig,
  useDocumentTitle,
  useScrollAnimation,
  websiteSchema
};
//# sourceMappingURL=index.mjs.map